<template>
  <div class="document">
    <div class="header">
      <EsHeader>
        <template #header>
          <div class="header-title" v-if="!editCurrentPanel">
            Specifications Details
          </div>
          <div v-else>
            Edit Specifications Details
          </div>
        </template>
        <template #actions
          v-if="!editCurrentPanel && !loading && !loadingError">
          <ul>
            <li v-if="hasModule('SYS:SYS:CONFIGMGR:EDIT')"
              class="p-narrow no-border" style="padding-right: 0">
              <div class="list-content">
                <a role="button" tabindex="0"
                  @keyup.enter="editPanel()"
                  title="Edit"
                  @click="editPanel()">
                  <font-awesome-icon :icon="['far', 'edit']" size="lg"/>
                </a>
              </div>
            </li>
            <li class="p-narrow no-border">
              <div class="list-content">
                <a role="button" tabindex="0"
                  @keyup.enter="goBackToSubmissionSpecifications()"
                  title="Go Back"
                  @click="goBackToSubmissionSpecifications()">
                  <font-awesome-icon :icon="['far', 'angle-double-left']" size="2x"/>
                </a>
              </div>
            </li>
          </ul>
        </template>
      </EsHeader>
    </div>
    <es-loader v-if="loading" style="margin: 5rem"/>
    <es-exception
      v-else-if="loadingError"
      exceptionMessage="There was an issue. Please try again or contact your administrator."
      margin="0"
      style="padding: 5rem"
    />
    <div v-else class="content">
      <div>
        <es-form style="padding: 1.15rem 0rem">
          <template #es-form-header>
            <div class="header-container">
              <div class="title is-6">
                {{ receivedPersonType.name }} Person Type
              </div>
            </div>
          </template>
          <template #es-form>
            <table role="presentation">
              <tbody>
                <tr>
                  <td class="property has-text-grey top-row no-border top-padding">
                    Name
                  </td>
                  <td class="top-row no-border details">
                    <div class="control">
                      <input
                        class="input is-static"
                        type="text"
                        :class="{ 'is-readonly' : editCurrentPanel }"
                        readonly
                        v-model="specificationType.name"
                        :title="specificationType.name"
                        aria-label="Name"
                      />
                    </div>
                  </td>
                </tr>
                <tr>
                  <td class="property has-text-grey no-border">
                    Submission Purpose
                  </td>
                  <td class="no-border">
                    <div class="control">
                      <input
                        class="input input is-static"
                        type="text"
                        :class="{ 'is-readonly' : editCurrentPanel }"
                        readonly
                        :value="specificationType.purpose === 'ASSIGN' ? 'Assignment'
                          : specificationType.purpose === 'ENROLL' ? 'Enrollment'
                          : specificationType.purpose === 'SEARCH' ? 'Search'
                          : specificationType.purpose === 'DMATCH' ? 'DirectMatch'
                          : false"
                        aria-label="Submission Purpose"
                      />
                    </div>
                  </td>
                </tr>
                <tr>
                  <td class="property has-text-grey no-border">
                    Header Record Indicator
                  </td>
                  <td class="no-border">
                    <div class="control">
                      <input
                        autocomplete="off"
                        class="input"
                        maxlength="10"
                        onpaste="return false;"
                        ondrop="return false;"
                        style="width: 30%"
                        type="text"
                        :class="{
                           'is-static disable-cursor': !editCurrentPanel,
                           'is-danger' : hasErrorHeader
                        }"
                        :readonly="!editCurrentPanel"
                        @keypress="preventSpecialCharacters($event)"
                        v-model="specificationType.headerInd"
                        :title="specificationType.headerInd"
                        aria-label="Header Record Indicator"
                      />
                    </div>
                    <span v-if="hasErrorHeader" class="help is-danger">
                      {{ msgHeader }}
                    </span>
                  </td>
                </tr>
                <tr>
                  <td class="property has-text-grey no-border">
                    Detailed Record Indicator
                  </td>
                  <td class="no-border">
                    <div class="control">
                      <input
                        autocomplete="off"
                        class="input"
                        maxlength="10"
                        onpaste="return false;"
                        ondrop="return false;"
                        style="width: 30%"
                        type="text"
                        :class="{
                           'is-static disable-cursor': !editCurrentPanel,
                           'is-danger' : hasErrorDetail
                        }"
                        :readonly="!editCurrentPanel"
                        @keypress="preventSpecialCharacters($event)"
                        v-model="specificationType.detailInd"
                        :title="specificationType.detailInd"
                        aria-label="Detailed Record Indicator"
                      />
                    </div>
                    <span v-if="hasErrorDetail" class="help is-danger">
                      {{ msgDetail }}
                    </span>
                  </td>
                </tr>
                <tr>
                  <td class="property has-text-grey no-border">
                    Footer Record Indicator
                  </td>
                  <td class="no-border">
                    <div class="control">
                      <input
                        autocomplete="off"
                        class="input"
                        maxlength="10"
                        onpaste="return false;"
                        ondrop="return false;"
                        style="width: 30%"
                        type="text"
                        :title="specificationType.footerInd"
                        :class="{
                           'is-static disable-cursor': !editCurrentPanel,
                           'is-danger' : hasErrorFooter
                        }"
                        :readonly="!editCurrentPanel"
                        @keypress="preventSpecialCharacters($event)"
                        v-model="specificationType.footerInd"
                        aria-label="Footer Record Indicator"
                      />
                    </div>
                    <span v-if="hasErrorFooter" class="help is-danger">
                      {{ msgFooter }}
                    </span>
                  </td>
                </tr>
                <tr>
                  <td class="property has-text-grey no-border">
                    Status
                  </td>
                  <td class="no-border">
                    <div class="control">
                      <div
                        class="buttons has-addons"
                        v-if="editCurrentPanel"
                      >
                        <button
                          class="button button--form"
                          v-for="(option, index) in booleanOptions"
                          :class="{ 'is-active': specificationType.enabled === option.code }"
                          :key="index"
                          @click="specificationType.enabled = option.code"
                        >
                          {{ option.description2 }}
                        </button>
                      </div>
                      <input
                        v-else
                        class="input"
                        :class="{ 'is-static disable-cursor': !editCurrentPanel }"
                        type="text"
                        title="Status"
                        :readonly="!editCurrentPanel"
                        :value="specificationType.enabled === true ? 'Enabled' : 'Disabled'"
                        aria-label="Status"
                      />
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </template>
        </es-form>
        <es-form class="partition-border">
          <template #es-form-header>
            <div class="header-container">
              <div class="title is-6">
                Person Information
              </div>
              <div class="icon-right">
                <a role="button" tabindex="0"
                  @keyup.enter="toggleField('personInformation')()">
                  <font-awesome-icon :icon="['far', 'caret-up']" size="lg"
                    @click="toggleField('personInformation')()"
                    v-if="personInformation"
                    title="Hide Details"
                  />
                  <font-awesome-icon :icon="['far', 'caret-down']" size="lg"
                    @click="toggleField('personInformation')()"
                    v-if="!personInformation"
                    title="View Details"
                  />
                </a>
              </div>
            </div>
          </template>
          <template #es-form>
            <table v-if="personInformation">
              <tbody>
                <tr>
                  <th class="rule left-padding">
                    eScholar Field
                  </th>
                  <th class="rule">
                    Display Label
                  </th>
                  <th class="rule">
                    Enabled
                  </th>
                  <th class="rule">
                    Required
                  </th>
                </tr>
                <tr :class="{ 'bottom-border': !editCurrentPanel }">
                  <td class="fields has-text-grey">
                    {{ getField('FIRST_NAME').name }}
                  </td>
                  <es-loader v-if="!getField('FIRST_NAME')" class="iconCenter"/>
                  <template v-else>
                    <td>
                      <div v-if="editCurrentPanel">
                        <input
                          class="input"
                          type="text"
                          v-model="getField('FIRST_NAME').customLabel"
                          :title="getField('FIRST_NAME').customLabel"
                          aria-label="Display Label"
                        />
                      </div>
                      <div v-else class="disable-cursor">
                        <input
                          class="input"
                          :class="{ 'is-static': !editCurrentPanel }"
                          :readonly="!editCurrentPanel"
                          :value="getField('FIRST_NAME').customLabel"
                          :title="getField('FIRST_NAME').customLabel"
                          aria-label="Display Label"
                        />
                      </div>
                    </td>
                    <td>
                      <div v-if="editCurrentPanel">
                        <button
                          class="button button--form"
                          :disabled="getField('FIRST_NAME').enabledReadOnly"
                          v-for="(option, index) in booleanOptions"
                          @click="getField('FIRST_NAME').enabled = option.code;
                            validateRequiredButton('FIRST_NAME')"
                          :class="{ 'is-active': getField('FIRST_NAME').enabled === option.code }"
                          :key="index"
                        >
                          {{ option.description }}
                        </button>
                      </div>
                      <div v-else class="disable-cursor">
                        {{ getField('FIRST_NAME').enabled === true ? 'Yes' : 'No' }}
                      </div>
                    </td>
                    <td>
                      <div v-if="editCurrentPanel">
                        <button
                          class="button button--form"
                          :disabled="getField('FIRST_NAME').requiredReadOnly"
                          v-for="(option, index) in booleanOptions"
                          @click="getField('FIRST_NAME').required = option.code;
                            validateRequiredButton('FIRST_NAME', 'NOTENABLED')"
                          :class="{ 'is-active': getField('FIRST_NAME').required === option.code }"
                          :key="index"
                        >
                          {{ option.description }}
                        </button>
                      </div>
                      <div v-else class="disable-cursor">
                        {{ getField('FIRST_NAME').required === true ? 'Yes' : 'No' }}
                      </div>
                    </td>
                  </template>
                </tr>
                <tr :class="{ 'bottom-border': !editCurrentPanel }">
                  <td class="fields has-text-grey">
                    {{ getField('MIDDLE_NAME').name }}
                  </td>
                  <es-loader v-if="!getField('MIDDLE_NAME')" class="iconCenter"/>
                  <template v-else>
                    <td>
                      <div v-if="editCurrentPanel">
                        <input
                          class="input"
                          type="text"
                          v-model="getField('MIDDLE_NAME').customLabel"
                          :title="getField('MIDDLE_NAME').customLabel"
                          aria-label="Display Label"
                        />
                      </div>
                      <div v-else class="disable-cursor">
                        <input
                          class="input"
                          :class="{ 'is-static': !editCurrentPanel }"
                          :readonly="!editCurrentPanel"
                          :value="getField('MIDDLE_NAME').customLabel"
                          :title="getField('MIDDLE_NAME').customLabel"
                          aria-label="Display Label"
                        />
                      </div>
                    </td>
                    <td>
                      <div v-if="editCurrentPanel">
                        <button
                          class="button button--form"
                          :disabled="getField('MIDDLE_NAME').enabledReadOnly"
                          v-for="(option, index) in booleanOptions"
                          @click="getField('MIDDLE_NAME').enabled = option.code;
                            validateRequiredButton('MIDDLE_NAME')"
                          :class="{ 'is-active': getField('MIDDLE_NAME').enabled === option.code }"
                          :key="index"
                        >
                          {{ option.description }}
                        </button>
                      </div>
                      <div v-else class="disable-cursor">
                        {{ getField('MIDDLE_NAME').enabled === true ? 'Yes' : 'No' }}
                      </div>
                    </td>
                    <td>
                      <div v-if="editCurrentPanel">
                        <button
                          class="button button--form"
                          :disabled="getField('MIDDLE_NAME').requiredReadOnly"
                          v-for="(option, index) in booleanOptions"
                          @click="getField('MIDDLE_NAME').required = option.code;
                            validateRequiredButton('MIDDLE_NAME', 'NOTENABLED')"
                          :class="{ 'is-active': getField('MIDDLE_NAME').required === option.code }"
                          :key="index"
                        >
                          {{ option.description }}
                        </button>
                      </div>
                      <div v-else class="disable-cursor">
                        {{ getField('MIDDLE_NAME').required === true ? 'Yes' : 'No' }}
                      </div>
                    </td>
                  </template>
                </tr>
                <tr :class="{ 'bottom-border': !editCurrentPanel }">
                  <td class="fields has-text-grey">
                    {{ getField('LAST_NAME').name }}
                  </td>
                  <es-loader v-if="!getField('LAST_NAME')" class="iconCenter"/>
                  <template v-else>
                    <td>
                      <div v-if="editCurrentPanel">
                        <input
                          class="input"
                          type="text"
                          v-model="getField('LAST_NAME').customLabel"
                          :title="getField('LAST_NAME').customLabel"
                          aria-label="Display Label"
                        />
                      </div>
                      <div v-else class="disable-cursor">
                        <input
                          class="input"
                          :class="{ 'is-static': !editCurrentPanel }"
                          :readonly="!editCurrentPanel"
                          :value="getField('LAST_NAME').customLabel"
                          :title="getField('LAST_NAME').customLabel"
                          aria-label="Display Label"
                        />
                      </div>
                    </td>
                    <td>
                      <div v-if="editCurrentPanel">
                        <button
                          class="button button--form"
                          :disabled="getField('LAST_NAME').enabledReadOnly"
                          v-for="(option, index) in booleanOptions"
                          @click="getField('LAST_NAME').enabled = option.code;
                            validateRequiredButton('LAST_NAME')"
                          :class="{ 'is-active': getField('LAST_NAME').enabled === option.code }"
                          :key="index"
                        >
                          {{ option.description }}
                        </button>
                      </div>
                      <div v-else class="disable-cursor">
                        {{ getField('LAST_NAME').enabled === true ? 'Yes' : 'No' }}
                      </div>
                    </td>
                    <td>
                      <div v-if="editCurrentPanel">
                        <button
                          class="button button--form"
                          :disabled="getField('LAST_NAME').requiredReadOnly"
                          v-for="(option, index) in booleanOptions"
                          @click="getField('LAST_NAME').required = option.code;
                            validateRequiredButton('LAST_NAME', 'NOTENABLED')"
                          :class="{ 'is-active': getField('LAST_NAME').required === option.code }"
                          :key="index"
                        >
                          {{ option.description }}
                        </button>
                      </div>
                      <div v-else class="disable-cursor">
                        {{ getField('LAST_NAME').required === true ? 'Yes' : 'No' }}
                      </div>
                    </td>
                  </template>
                </tr>
                <tr :class="{ 'bottom-border': !editCurrentPanel }">
                  <td class="fields has-text-grey">
                    {{ getField('SUFFIX').name }}
                  </td>
                  <es-loader v-if="!getField('SUFFIX')" class="iconCenter"/>
                  <template v-else>
                    <td>
                      <div v-if="editCurrentPanel">
                        <input
                          class="input"
                          type="text"
                          v-model="getField('SUFFIX').customLabel"
                          :title="getField('SUFFIX').customLabel"
                          aria-label="Display Label"
                        />
                      </div>
                      <div v-else class="disable-cursor">
                        <input
                          class="input"
                          :class="{ 'is-static': !editCurrentPanel }"
                          :readonly="!editCurrentPanel"
                          :value="getField('SUFFIX').customLabel"
                          :title="getField('SUFFIX').customLabel"
                          aria-label="Display Label"
                        />
                      </div>
                    </td>
                    <td>
                      <div v-if="editCurrentPanel">
                        <button
                          class="button button--form"
                          :disabled="getField('SUFFIX').enabledReadOnly"
                          v-for="(option, index) in booleanOptions"
                          @click="getField('SUFFIX').enabled = option.code;
                            validateRequiredButton('SUFFIX')"
                          :class="{ 'is-active': getField('SUFFIX').enabled === option.code }"
                          :key="index"
                        >
                          {{ option.description }}
                        </button>
                      </div>
                      <div v-else class="disable-cursor">
                        {{ getField('SUFFIX').enabled === true ? 'Yes' : 'No' }}
                      </div>
                    </td>
                    <td>
                      <div v-if="editCurrentPanel">
                        <button
                          class="button button--form"
                          :disabled="getField('SUFFIX').requiredReadOnly"
                          v-for="(option, index) in booleanOptions"
                          @click="getField('SUFFIX').required = option.code;
                            validateRequiredButton('SUFFIX', 'NOTENABLED')"
                          :class="{ 'is-active': getField('SUFFIX').required === option.code }"
                          :key="index"
                        >
                          {{ option.description }}
                        </button>
                      </div>
                      <div v-else class="disable-cursor">
                        {{ getField('SUFFIX').required === true ? 'Yes' : 'No' }}
                      </div>
                    </td>
                  </template>
                </tr>
                <tr :class="{ 'bottom-border': !editCurrentPanel }">
                  <td class="fields has-text-grey">
                    {{ getField('PREVIOUS_LAST_NAME').name }}
                  </td>
                  <es-loader v-if="!getField('PREVIOUS_LAST_NAME')" class="iconCenter"/>
                  <template v-else>
                    <td>
                      <div v-if="editCurrentPanel">
                        <input
                          class="input"
                          type="text"
                          v-model="getField('PREVIOUS_LAST_NAME').customLabel"
                          :title="getField('PREVIOUS_LAST_NAME').customLabel"
                          aria-label="Display Label"
                        />
                      </div>
                      <div v-else class="disable-cursor">
                        <input
                          class="input"
                          :class="{ 'is-static': !editCurrentPanel }"
                          :readonly="!editCurrentPanel"
                          :value="getField('PREVIOUS_LAST_NAME').customLabel"
                          :title="getField('PREVIOUS_LAST_NAME').customLabel"
                          aria-label="Display Label"
                        />
                      </div>
                    </td>
                    <td>
                      <div v-if="editCurrentPanel">
                        <button
                          class="button button--form"
                          :disabled="getField('PREVIOUS_LAST_NAME').enabledReadOnly"
                          v-for="(option, index) in booleanOptions"
                          @click="getField('PREVIOUS_LAST_NAME').enabled = option.code;
                            validateRequiredButton('PREVIOUS_LAST_NAME')"
                          :class="{ 'is-active': getField('PREVIOUS_LAST_NAME')
                            .enabled === option.code }"
                          :key="index"
                        >
                          {{ option.description }}
                        </button>
                      </div>
                      <div v-else class="disable-cursor">
                        {{ getField('PREVIOUS_LAST_NAME').enabled === true ? 'Yes' : 'No' }}
                      </div>
                    </td>
                    <td>
                      <div v-if="editCurrentPanel">
                        <button
                          class="button button--form"
                          :disabled="getField('PREVIOUS_LAST_NAME').requiredReadOnly"
                          v-for="(option, index) in booleanOptions"
                          @click="getField('PREVIOUS_LAST_NAME').required = option.code;
                            validateRequiredButton('PREVIOUS_LAST_NAME', 'NOTENABLED')"
                          :class="{ 'is-active': getField('PREVIOUS_LAST_NAME')
                            .required === option.code }"
                          :key="index"
                        >
                          {{ option.description }}
                        </button>
                      </div>
                      <div v-else class="disable-cursor">
                        {{ getField('PREVIOUS_LAST_NAME').required === true ? 'Yes' : 'No' }}
                      </div>
                    </td>
                  </template>
                </tr>
                <tr :class="{ 'bottom-border': !editCurrentPanel }">
                  <td class="fields has-text-grey">
                    {{ getField('FULL_NAME').name }}
                  </td>
                  <es-loader v-if="!getField('FULL_NAME')" class="iconCenter"/>
                  <template v-else>
                    <td>
                      <div v-if="editCurrentPanel">
                        <input
                          class="input"
                          type="text"
                          v-model="getField('FULL_NAME').customLabel"
                          :title="getField('FULL_NAME').customLabel"
                          aria-label="Display Label"
                        />
                      </div>
                      <div v-else class="disable-cursor">
                        <input
                          class="input"
                          :class="{ 'is-static': !editCurrentPanel }"
                          :readonly="!editCurrentPanel"
                          :value="getField('FULL_NAME').customLabel"
                          :title="getField('FULL_NAME').customLabel"
                          aria-label="Display Label"
                        />
                      </div>
                    </td>
                    <td>
                      <div v-if="editCurrentPanel">
                        <button
                          class="button button--form"
                          :disabled="getField('FULL_NAME').enabledReadOnly"
                          v-for="(option, index) in booleanOptions"
                          @click="getField('FULL_NAME').enabled = option.code;
                            validateRequiredButton('FULL_NAME')"
                          :class="{ 'is-active': getField('FULL_NAME').enabled === option.code }"
                          :key="index"
                        >
                          {{ option.description }}
                        </button>
                      </div>
                      <div v-else class="disable-cursor">
                        {{ getField('FULL_NAME').enabled === true ? 'Yes' : 'No' }}
                      </div>
                    </td>
                    <td>
                      <div v-if="editCurrentPanel">
                        <button
                          class="button button--form"
                          :disabled="getField('FULL_NAME').requiredReadOnly"
                          v-for="(option, index) in booleanOptions"
                          @click="getField('FULL_NAME').required = option.code;
                            validateRequiredButton('FULL_NAME', 'NOTENABLED')"
                          :class="{ 'is-active': getField('FULL_NAME').required === option.code }"
                          :key="index"
                        >
                          {{ option.description }}
                        </button>
                      </div>
                      <div v-else class="disable-cursor">
                        {{ getField('FULL_NAME').required === true ? 'Yes' : 'No' }}
                      </div>
                    </td>
                  </template>
                </tr>
                <tr :class="{ 'bottom-border': !editCurrentPanel }">
                  <td class="fields has-text-grey">
                    {{ getField('AKA_NAME').name }}
                  </td>
                  <es-loader v-if="!getField('AKA_NAME')" class="iconCenter"/>
                  <template v-else>
                    <td>
                      <div v-if="editCurrentPanel">
                        <input
                          class="input"
                          type="text"
                          v-model="getField('AKA_NAME').customLabel"
                          :title="getField('AKA_NAME').customLabel"
                          aria-label="Display Label"
                        />
                      </div>
                      <div v-else class="disable-cursor">
                        <input
                          class="input"
                          :class="{ 'is-static': !editCurrentPanel }"
                          :readonly="!editCurrentPanel"
                          :value="getField('AKA_NAME').customLabel"
                          :title="getField('AKA_NAME').customLabel"
                          aria-label="Display Label"
                        />
                      </div>
                    </td>
                    <td>
                      <div v-if="editCurrentPanel">
                        <button
                          class="button button--form"
                          :disabled="getField('AKA_NAME').enabledReadOnly"
                          v-for="(option, index) in booleanOptions"
                          @click="getField('AKA_NAME').enabled = option.code;
                            validateRequiredButton('AKA_NAME')"
                          :class="{ 'is-active': getField('AKA_NAME').enabled === option.code }"
                          :key="index"
                        >
                          {{ option.description }}
                        </button>
                      </div>
                      <div v-else class="disable-cursor">
                        {{ getField('AKA_NAME').enabled === true ? 'Yes' : 'No' }}
                      </div>
                    </td>
                    <td>
                      <div v-if="editCurrentPanel">
                        <button
                          class="button button--form"
                          :disabled="getField('AKA_NAME').requiredReadOnly"
                          v-for="(option, index) in booleanOptions"
                          @click="getField('AKA_NAME').required = option.code;
                            validateRequiredButton('AKA_NAME', 'NOTENABLED')"
                          :class="{ 'is-active': getField('AKA_NAME').required === option.code }"
                          :key="index"
                        >
                          {{ option.description }}
                        </button>
                      </div>
                      <div v-else class="disable-cursor">
                        {{ getField('AKA_NAME').required === true ? 'Yes' : 'No' }}
                      </div>
                    </td>
                  </template>
                </tr>
                <tr :class="{ 'bottom-border': !editCurrentPanel }">
                  <td class="fields has-text-grey">
                    {{ getField('GENDER_CODE').name }}
                  </td>
                  <es-loader v-if="!getField('GENDER_CODE')" class="iconCenter"/>
                  <template v-else>
                    <td>
                      <div v-if="editCurrentPanel">
                        <input
                          class="input"
                          type="text"
                          v-model="getField('GENDER_CODE').customLabel"
                          :title="getField('GENDER_CODE').customLabel"
                          aria-label="Display Label"
                        />
                      </div>
                      <div v-else class="disable-cursor">
                        <input
                          class="input"
                          :class="{ 'is-static': !editCurrentPanel }"
                          :readonly="!editCurrentPanel"
                          :value="getField('GENDER_CODE').customLabel"
                          :title="getField('GENDER_CODE').customLabel"
                          aria-label="Display Label"
                        />
                      </div>
                    </td>
                    <td>
                      <div v-if="editCurrentPanel">
                        <button
                          class="button button--form"
                          :disabled="getField('GENDER_CODE').enabledReadOnly"
                          v-for="(option, index) in booleanOptions"
                          @click="getField('GENDER_CODE').enabled = option.code;
                            validateRequiredButton('GENDER_CODE')"
                          :class="{ 'is-active': getField('GENDER_CODE').enabled === option.code }"
                          :key="index"
                        >
                          {{ option.description }}
                        </button>
                      </div>
                      <div v-else class="disable-cursor">
                        {{ getField('GENDER_CODE').enabled === true ? 'Yes' : 'No' }}
                      </div>
                    </td>
                    <td>
                      <div v-if="editCurrentPanel">
                        <button
                          class="button button--form"
                          :disabled="getField('GENDER_CODE').requiredReadOnly"
                          v-for="(option, index) in booleanOptions"
                          @click="getField('GENDER_CODE').required = option.code;
                            validateRequiredButton('GENDER_CODE', 'NOTENABLED')"
                          :class="{ 'is-active': getField('GENDER_CODE').required === option.code }"
                          :key="index"
                        >
                          {{ option.description }}
                        </button>
                      </div>
                      <div v-else class="disable-cursor">
                        {{ getField('GENDER_CODE').required === true ? 'Yes' : 'No' }}
                      </div>
                    </td>
                  </template>
                </tr>
                <tr :class="{ 'bottom-border': !editCurrentPanel }">
                  <td class="fields has-text-grey">
                    {{ getField('DATE_OF_BIRTH').name }}
                  </td>
                  <es-loader v-if="!getField('DATE_OF_BIRTH')" class="iconCenter"/>
                  <template v-else>
                    <td>
                      <div v-if="editCurrentPanel">
                        <input
                          class="input"
                          type="text"
                          v-model="getField('DATE_OF_BIRTH').customLabel"
                          :titlel="getField('DATE_OF_BIRTH').customLabel"
                          aria-label="Display Label"
                        />
                      </div>
                      <div v-else class="disable-cursor">
                        <input
                          class="input"
                          :class="{ 'is-static': !editCurrentPanel }"
                          :readonly="!editCurrentPanel"
                          :value="getField('DATE_OF_BIRTH').customLabel"
                          :title="getField('DATE_OF_BIRTH').customLabel"
                          aria-label="Display Label"
                        />
                      </div>
                    </td>
                    <td>
                      <div v-if="editCurrentPanel">
                        <button
                          class="button button--form"
                          :disabled="getField('DATE_OF_BIRTH').enabledReadOnly"
                          v-for="(option, index) in booleanOptions"
                          @click="getField('DATE_OF_BIRTH').enabled = option.code;
                            validateRequiredButton('DATE_OF_BIRTH')"
                          :class="{'is-active': getField('DATE_OF_BIRTH').enabled === option.code}"
                          :key="index"
                        >
                          {{ option.description }}
                        </button>
                      </div>
                      <div v-else class="disable-cursor">
                        {{ getField('DATE_OF_BIRTH').enabled === true ? 'Yes' : 'No' }}
                      </div>
                    </td>
                    <td>
                      <div v-if="editCurrentPanel">
                        <button
                          class="button button--form"
                          :disabled="getField('DATE_OF_BIRTH').requiredReadOnly"
                          v-for="(option, index) in booleanOptions"
                          @click="getField('DATE_OF_BIRTH').required = option.code;
                            validateRequiredButton('DATE_OF_BIRTH', 'NOTENABLED')"
                          :class="{'is-active': getField('DATE_OF_BIRTH').required === option.code}"
                          :key="index"
                        >
                          {{ option.description }}
                        </button>
                      </div>
                      <div v-else class="disable-cursor">
                        {{ getField('DATE_OF_BIRTH').required === true ? 'Yes' : 'No' }}
                      </div>
                    </td>
                  </template>
                </tr>
                <tr :class="{ 'bottom-border': !editCurrentPanel }">
                  <td class="fields has-text-grey">
                    {{ getField('SOCIAL_SECURITY_NUMBER').name }}
                  </td>
                  <es-loader v-if="!getField('SOCIAL_SECURITY_NUMBER')" class="iconCenter"/>
                  <template v-else>
                    <td>
                      <div v-if="editCurrentPanel">
                        <input
                          class="input"
                          type="text"
                          v-model="getField('SOCIAL_SECURITY_NUMBER').customLabel"
                          :title="getField('SOCIAL_SECURITY_NUMBER').customLabel"
                          aria-label="Display Label"
                        />
                      </div>
                      <div v-else class="disable-cursor">
                        <input
                          class="input"
                          :class="{ 'is-static': !editCurrentPanel }"
                          :readonly="!editCurrentPanel"
                          :value="getField('SOCIAL_SECURITY_NUMBER').customLabel"
                          :title="getField('SOCIAL_SECURITY_NUMBER').customLabel"
                          aria-label="Display Label"
                        />
                      </div>
                    </td>
                    <td>
                      <div v-if="editCurrentPanel">
                        <button
                          class="button button--form"
                          :disabled="getField('SOCIAL_SECURITY_NUMBER').enabledReadOnly"
                          v-for="(option, index) in booleanOptions"
                          @click="getField('SOCIAL_SECURITY_NUMBER').enabled = option.code;
                            validateRequiredButton('SOCIAL_SECURITY_NUMBER')"
                          :class="{ 'is-active': getField('SOCIAL_SECURITY_NUMBER')
                            .enabled === option.code }"
                          :key="index"
                        >
                          {{ option.description }}
                        </button>
                      </div>
                      <div v-else class="disable-cursor">
                        {{ getField('SOCIAL_SECURITY_NUMBER').enabled === true ? 'Yes' : 'No' }}
                      </div>
                    </td>
                    <td>
                      <div v-if="editCurrentPanel">
                        <button
                          class="button button--form"
                          :disabled="getField('SOCIAL_SECURITY_NUMBER').requiredReadOnly"
                          v-for="(option, index) in booleanOptions"
                          @click="getField('SOCIAL_SECURITY_NUMBER').required = option.code;
                            validateRequiredButton('SOCIAL_SECURITY_NUMBER', 'NOTENABLED')"
                          :class="{ 'is-active': getField('SOCIAL_SECURITY_NUMBER')
                            .required === option.code }"
                          :key="index"
                        >
                          {{ option.description }}
                        </button>
                      </div>
                      <div v-else class="disable-cursor">
                        {{ getField('SOCIAL_SECURITY_NUMBER').required === true ? 'Yes' : 'No' }}
                      </div>
                    </td>
                  </template>
                </tr>
                <tr :class="{ 'bottom-border': !editCurrentPanel }">
                  <td class="fields has-text-grey">
                    {{ getField('ETHNICITY_INDICATOR').name }}
                  </td>
                  <es-loader v-if="!getField('ETHNICITY_INDICATOR')" class="iconCenter"/>
                  <template v-else>
                    <td>
                      <div v-if="editCurrentPanel">
                        <input
                          class="input"
                          type="text"
                          v-model="getField('ETHNICITY_INDICATOR').customLabel"
                          :title="getField('ETHNICITY_INDICATOR').customLabel"
                          aria-label="Display Label"
                        />
                      </div>
                      <div v-else class="disable-cursor">
                        <input
                          class="input"
                          :class="{ 'is-static': !editCurrentPanel }"
                          :readonly="!editCurrentPanel"
                          :value="getField('ETHNICITY_INDICATOR').customLabel"
                          :title="getField('ETHNICITY_INDICATOR').customLabel"
                          aria-label="Display Label"
                        />
                      </div>
                    </td>
                    <td>
                      <div v-if="editCurrentPanel">
                        <button
                          class="button button--form"
                          :disabled="getField('ETHNICITY_INDICATOR').enabledReadOnly"
                          v-for="(option, index) in booleanOptions"
                          @click="getField('ETHNICITY_INDICATOR').enabled = option.code;
                            validateRequiredButton('ETHNICITY_INDICATOR')"
                          :class="{ 'is-active': getField('ETHNICITY_INDICATOR')
                            .enabled === option.code }"
                          :key="index"
                        >
                          {{ option.description }}
                        </button>
                      </div>
                      <div v-else class="disable-cursor">
                        {{ getField('ETHNICITY_INDICATOR').enabled === true ? 'Yes' : 'No' }}
                      </div>
                    </td>
                    <td>
                      <div v-if="editCurrentPanel">
                        <button
                          class="button button--form"
                          :disabled="getField('ETHNICITY_INDICATOR').requiredReadOnly"
                          v-for="(option, index) in booleanOptions"
                          @click="getField('ETHNICITY_INDICATOR').required = option.code;
                            validateRequiredButton('ETHNICITY_INDICATOR', 'NOTENABLED')"
                          :class="{ 'is-active': getField('ETHNICITY_INDICATOR')
                            .required === option.code }"
                          :key="index"
                        >
                          {{ option.description }}
                        </button>
                      </div>
                      <div v-else class="disable-cursor">
                        {{ getField('ETHNICITY_INDICATOR').required === true ? 'Yes' : 'No' }}
                      </div>
                    </td>
                  </template>
                </tr>
                <tr :class="{ 'bottom-border': !editCurrentPanel }">
                  <td class="fields has-text-grey">
                    {{ getField('RACE_ETHNIC_CODE').name }}
                  </td>
                  <es-loader v-if="!getField('RACE_ETHNIC_CODE')" class="iconCenter"/>
                  <template v-else>
                    <td>
                      <div v-if="editCurrentPanel">
                        <input
                          class="input"
                          type="text"
                          v-model="getField('RACE_ETHNIC_CODE').customLabel"
                          :title="getField('RACE_ETHNIC_CODE').customLabel"
                          aria-label="Display Label"
                        />
                      </div>
                      <div v-else class="disable-cursor">
                        <input
                          class="input"
                          :class="{ 'is-static': !editCurrentPanel }"
                          :readonly="!editCurrentPanel"
                          :value="getField('RACE_ETHNIC_CODE').customLabel"
                          :title="getField('RACE_ETHNIC_CODE').customLabel"
                          aria-label="Display Label"
                        />
                      </div>
                    </td>
                    <td>
                      <div v-if="editCurrentPanel">
                        <button
                          class="button button--form"
                          :disabled="getField('RACE_ETHNIC_CODE').enabledReadOnly"
                          v-for="(option, index) in booleanOptions"
                          @click="getField('RACE_ETHNIC_CODE').enabled = option.code;
                            validateRequiredButton('RACE_ETHNIC_CODE')"
                          :class="{ 'is-active': getField('RACE_ETHNIC_CODE')
                            .enabled === option.code }"
                          :key="index"
                        >
                          {{ option.description }}
                        </button>
                      </div>
                      <div v-else class="disable-cursor">
                        {{ getField('RACE_ETHNIC_CODE').enabled === true ? 'Yes' : 'No' }}
                      </div>
                    </td>
                    <td>
                      <div v-if="editCurrentPanel">
                        <button
                          class="button button--form"
                          :disabled="getField('RACE_ETHNIC_CODE').requiredReadOnly"
                          v-for="(option, index) in booleanOptions"
                          @click="getField('RACE_ETHNIC_CODE').required = option.code;
                            validateRequiredButton('RACE_ETHNIC_CODE', 'NOTENABLED')"
                          :class="{ 'is-active': getField('RACE_ETHNIC_CODE')
                            .required === option.code }"
                          :key="index"
                        >
                          {{ option.description }}
                        </button>
                      </div>
                      <div v-else class="disable-cursor">
                        {{ getField('RACE_ETHNIC_CODE').required === true ? 'Yes' : 'No' }}
                      </div>
                    </td>
                  </template>
                </tr>
                <tr :class="{ 'bottom-border': !editCurrentPanel }">
                  <td class="fields has-text-grey">
                    {{ getField('RACE2_CODE').name }}
                  </td>
                  <es-loader v-if="!getField('RACE2_CODE')" class="iconCenter"/>
                  <template v-else>
                    <td>
                      <div v-if="editCurrentPanel">
                        <input
                          class="input"
                          type="text"
                          v-model="getField('RACE2_CODE').customLabel"
                          :title="getField('RACE2_CODE').customLabel"
                          aria-label="Display Label"
                        />
                      </div>
                      <div v-else class="disable-cursor">
                        <input
                          class="input"
                          :class="{ 'is-static': !editCurrentPanel }"
                          :readonly="!editCurrentPanel"
                          :value="getField('RACE2_CODE').customLabel"
                          :title="getField('RACE2_CODE').customLabel"
                          aria-label="Display Label"
                        />
                      </div>
                    </td>
                    <td>
                      <div v-if="editCurrentPanel">
                        <button
                          class="button button--form"
                          :disabled="getField('RACE2_CODE').enabledReadOnly"
                          v-for="(option, index) in booleanOptions"
                          @click="getField('RACE2_CODE').enabled = option.code;
                            validateRequiredButton('RACE2_CODE')"
                          :class="{ 'is-active': getField('RACE2_CODE').enabled === option.code }"
                          :key="index"
                        >
                          {{ option.description }}
                        </button>
                      </div>
                      <div v-else class="disable-cursor">
                        {{ getField('RACE2_CODE').enabled === true ? 'Yes' : 'No' }}
                      </div>
                    </td>
                    <td>
                      <div v-if="editCurrentPanel">
                        <button
                          class="button button--form"
                          :disabled="getField('RACE2_CODE').requiredReadOnly"
                          v-for="(option, index) in booleanOptions"
                          @click="getField('RACE2_CODE').required = option.code;
                            validateRequiredButton('RACE2_CODE', 'NOTENABLED')"
                          :class="{ 'is-active': getField('RACE2_CODE').required === option.code }"
                          :key="index"
                        >
                          {{ option.description }}
                        </button>
                      </div>
                      <div v-else class="disable-cursor">
                        {{ getField('RACE2_CODE').required === true ? 'Yes' : 'No' }}
                      </div>
                    </td>
                  </template>
                </tr>
                <tr :class="{ 'bottom-border': !editCurrentPanel }">
                  <td class="fields has-text-grey">
                    {{ getField('RACE3_CODE').name }}
                  </td>
                  <es-loader v-if="!getField('RACE3_CODE')" class="iconCenter"/>
                  <template v-else>
                    <td>
                      <div v-if="editCurrentPanel">
                        <input
                          class="input"
                          type="text"
                          v-model="getField('RACE3_CODE').customLabel"
                          :title="getField('RACE3_CODE').customLabel"
                          aria-label="Display Label"
                        />
                      </div>
                      <div v-else class="disable-cursor">
                        <input
                          class="input"
                          :class="{ 'is-static': !editCurrentPanel }"
                          :readonly="!editCurrentPanel"
                          :value="getField('RACE3_CODE').customLabel"
                          :title="getField('RACE3_CODE').customLabel"
                          aria-label="Display Label"
                        />
                      </div>
                    </td>
                    <td>
                      <div v-if="editCurrentPanel">
                        <button
                          class="button button--form"
                          :disabled="getField('RACE3_CODE').enabledReadOnly"
                          v-for="(option, index) in booleanOptions"
                          @click="getField('RACE3_CODE').enabled = option.code;
                            validateRequiredButton('RACE3_CODE')"
                          :class="{ 'is-active': getField('RACE3_CODE').enabled === option.code }"
                          :key="index"
                        >
                          {{ option.description }}
                        </button>
                      </div>
                      <div v-else class="disable-cursor">
                        {{ getField('RACE3_CODE').enabled === true ? 'Yes' : 'No' }}
                      </div>
                    </td>
                    <td>
                      <div v-if="editCurrentPanel">
                        <button
                          class="button button--form"
                          :disabled="getField('RACE3_CODE').requiredReadOnly"
                          v-for="(option, index) in booleanOptions"
                          @click="getField('RACE3_CODE').required = option.code;
                            validateRequiredButton('RACE3_CODE', 'NOTENABLED')"
                          :class="{ 'is-active': getField('RACE3_CODE').required === option.code }"
                          :key="index"
                        >
                          {{ option.description }}
                        </button>
                      </div>
                      <div v-else class="disable-cursor">
                        {{ getField('RACE3_CODE').required === true ? 'Yes' : 'No' }}
                      </div>
                    </td>
                  </template>
                </tr>
                <tr :class="{ 'bottom-border': !editCurrentPanel }">
                  <td class="fields has-text-grey">
                    {{ getField('RACE4_CODE').name }}
                  </td>
                  <es-loader v-if="!getField('RACE4_CODE')" class="iconCenter"/>
                  <template v-else>
                    <td>
                      <div v-if="editCurrentPanel">
                        <input
                          class="input"
                          type="text"
                          v-model="getField('RACE4_CODE').customLabel"
                          :title="getField('RACE4_CODE').customLabel"
                          aria-label="Display Label"
                        />
                      </div>
                      <div v-else class="disable-cursor">
                        <input
                          class="input"
                          :class="{ 'is-static': !editCurrentPanel }"
                          :readonly="!editCurrentPanel"
                          :value="getField('RACE4_CODE').customLabel"
                          :title="getField('RACE4_CODE').customLabel"
                          aria-label="Display Label"
                        />
                      </div>
                    </td>
                    <td>
                      <div v-if="editCurrentPanel">
                        <button
                          class="button button--form"
                          :disabled="getField('RACE4_CODE').enabledReadOnly"
                          v-for="(option, index) in booleanOptions"
                          @click="getField('RACE4_CODE').enabled = option.code;
                            validateRequiredButton('RACE4_CODE')"
                          :class="{ 'is-active': getField('RACE4_CODE').enabled === option.code }"
                          :key="index"
                        >
                          {{ option.description }}
                        </button>
                      </div>
                      <div v-else class="disable-cursor">
                        {{ getField('RACE4_CODE').enabled === true ? 'Yes' : 'No' }}
                      </div>
                    </td>
                    <td>
                      <div v-if="editCurrentPanel">
                        <button
                          class="button button--form"
                          :disabled="getField('RACE4_CODE').requiredReadOnly"
                          v-for="(option, index) in booleanOptions"
                          @click="getField('RACE4_CODE').required = option.code;
                            validateRequiredButton('RACE4_CODE', 'NOTENABLED')"
                          :class="{ 'is-active': getField('RACE4_CODE').required === option.code }"
                          :key="index"
                        >
                          {{ option.description }}
                        </button>
                      </div>
                      <div v-else class="disable-cursor">
                        {{ getField('RACE4_CODE').required === true ? 'Yes' : 'No' }}
                      </div>
                    </td>
                  </template>
                </tr>
                <tr :class="{ 'bottom-border': !editCurrentPanel }">
                  <td class="fields has-text-grey">
                    {{ getField('RACE5_CODE').name }}
                  </td>
                  <es-loader v-if="!getField('RACE5_CODE')" class="iconCenter"/>
                  <template v-else>
                    <td>
                      <div v-if="editCurrentPanel">
                        <input
                          class="input"
                          type="text"
                          v-model="getField('RACE5_CODE').customLabel"
                          :title="getField('RACE5_CODE').customLabel"
                          aria-label="Display Label"
                        />
                      </div>
                      <div v-else class="disable-cursor">
                        <input
                          class="input"
                          :class="{ 'is-static': !editCurrentPanel }"
                          :readonly="!editCurrentPanel"
                          :value="getField('RACE5_CODE').customLabel"
                          :title="getField('RACE5_CODE').customLabel"
                          aria-label="Display Label"
                        />
                      </div>
                    </td>
                    <td>
                      <div v-if="editCurrentPanel">
                        <button
                          class="button button--form"
                          :disabled="getField('RACE5_CODE').enabledReadOnly"
                          v-for="(option, index) in booleanOptions"
                          @click="getField('RACE5_CODE').enabled = option.code;
                            validateRequiredButton('RACE5_CODE')"
                          :class="{ 'is-active': getField('RACE5_CODE').enabled === option.code }"
                          :key="index"
                        >
                          {{ option.description }}
                        </button>
                      </div>
                      <div v-else class="disable-cursor">
                        {{ getField('RACE5_CODE').enabled === true ? 'Yes' : 'No' }}
                      </div>
                    </td>
                    <td>
                      <div v-if="editCurrentPanel">
                        <button
                          class="button button--form"
                          :disabled="getField('RACE5_CODE').requiredReadOnly"
                          v-for="(option, index) in booleanOptions"
                          @click="getField('RACE5_CODE').required = option.code;
                            validateRequiredButton('RACE5_CODE', 'NOTENABLED')"
                          :class="{ 'is-active': getField('RACE5_CODE').required === option.code }"
                          :key="index"
                        >
                          {{ option.description }}
                        </button>
                      </div>
                      <div v-else class="disable-cursor">
                        {{ getField('RACE5_CODE').required === true ? 'Yes' : 'No' }}
                      </div>
                    </td>
                  </template>
                </tr>
                <tr :class="{ 'bottom-border': !editCurrentPanel }">
                  <td class="fields has-text-grey">
                    {{ getField('ETHNIC_SUB_GROUP').name }}
                  </td>
                  <es-loader v-if="!getField('ETHNIC_SUB_GROUP')" class="iconCenter"/>
                  <template v-else>
                    <td>
                      <div v-if="editCurrentPanel">
                        <input
                          class="input"
                          type="text"
                          v-model="getField('ETHNIC_SUB_GROUP').customLabel"
                          :title="getField('ETHNIC_SUB_GROUP').customLabel"
                          aria-label="Display Label"
                        />
                      </div>
                      <div v-else class="disable-cursor">
                        <input
                          class="input"
                          :class="{ 'is-static': !editCurrentPanel }"
                          :readonly="!editCurrentPanel"
                          :value="getField('ETHNIC_SUB_GROUP').customLabel"
                          :title="getField('ETHNIC_SUB_GROUP').customLabel"
                          aria-label="Display Label"
                        />
                      </div>
                    </td>
                    <td>
                      <div v-if="editCurrentPanel">
                        <button
                          class="button button--form"
                          :disabled="getField('ETHNIC_SUB_GROUP').enabledReadOnly"
                          v-for="(option, index) in booleanOptions"
                          @click="getField('ETHNIC_SUB_GROUP').enabled = option.code;
                            validateRequiredButton('ETHNIC_SUB_GROUP')"
                          :class="{ 'is-active': getField('ETHNIC_SUB_GROUP')
                            .enabled === option.code }"
                          :key="index"
                        >
                          {{ option.description }}
                        </button>
                      </div>
                      <div v-else class="disable-cursor">
                        {{ getField('ETHNIC_SUB_GROUP').enabled === true ? 'Yes' : 'No' }}
                      </div>
                    </td>
                    <td>
                      <div v-if="editCurrentPanel">
                        <button
                          class="button button--form"
                          :disabled="getField('ETHNIC_SUB_GROUP').requiredReadOnly"
                          v-for="(option, index) in booleanOptions"
                          @click="getField('ETHNIC_SUB_GROUP').required = option.code;
                            validateRequiredButton('ETHNIC_SUB_GROUP', 'NOTENABLED')"
                          :class="{ 'is-active': getField('ETHNIC_SUB_GROUP')
                            .required === option.code }"
                          :key="index"
                        >
                          {{ option.description }}
                        </button>
                      </div>
                      <div v-else class="disable-cursor">
                        {{ getField('ETHNIC_SUB_GROUP').required === true ? 'Yes' : 'No' }}
                      </div>
                    </td>
                  </template>
                </tr>
                <tr>
                  <td class="fields has-text-grey">
                    {{ getField('STATE_STUDENT_ID').name }}
                  </td>
                  <es-loader v-if="!getField('STATE_STUDENT_ID')" class="iconCenter"/>
                  <template v-else>
                    <td>
                      <div v-if="editCurrentPanel">
                        <input
                          class="input"
                          type="text"
                          v-model="getField('STATE_STUDENT_ID').customLabel"
                          :title="getField('STATE_STUDENT_ID').customLabel"
                          aria-label="Display Label"
                        />
                      </div>
                      <div v-else class="disable-cursor">
                        <input
                          class="input"
                          :class="{ 'is-static': !editCurrentPanel }"
                          :readonly="!editCurrentPanel"
                          :value="getField('STATE_STUDENT_ID').customLabel"
                          :title="getField('STATE_STUDENT_ID').customLabel"
                          aria-label="Display Label"
                        />
                      </div>
                    </td>
                    <td>
                      <div v-if="editCurrentPanel">
                        <button
                          class="button button--form"
                          :disabled="getField('STATE_STUDENT_ID').enabledReadOnly"
                          v-for="(option, index) in booleanOptions"
                          @click="getField('STATE_STUDENT_ID').enabled = option.code;
                            validateRequiredButton('STATE_STUDENT_ID')"
                          :class="{ 'is-active': getField('STATE_STUDENT_ID')
                            .enabled === option.code }"
                          :key="index"
                        >
                          {{ option.description }}
                        </button>
                      </div>
                      <div v-else class="disable-cursor">
                        {{ getField('STATE_STUDENT_ID').enabled === true ? 'Yes' : 'No' }}
                      </div>
                    </td>
                    <td>
                      <div v-if="editCurrentPanel">
                        <button
                          class="button button--form"
                          :disabled="getField('STATE_STUDENT_ID').requiredReadOnly"
                          v-for="(option, index) in booleanOptions"
                          @click="getField('STATE_STUDENT_ID').required = option.code;
                            validateRequiredButton('STATE_STUDENT_ID', 'NOTENABLED')"
                          :class="{ 'is-active': getField('STATE_STUDENT_ID')
                            .required === option.code }"
                          :key="index"
                        >
                          {{ option.description }}
                        </button>
                      </div>
                      <div v-else class="disable-cursor">
                        {{ getField('STATE_STUDENT_ID').required === true ? 'Yes' : 'No' }}
                      </div>
                    </td>
                  </template>
                </tr>
              </tbody>
            </table>
          </template>
        </es-form>
        <es-form class="partition-border">
          <template #es-form-header>
            <div class="header-container">
              <div class="title is-6">
                Contact Information
              </div>
              <div class="icon-right">
                <a role="button" tabindex="0"
                  @keyup.enter="toggleField('contactInformation')()">
                  <font-awesome-icon :icon="['far', 'caret-up']" size="lg"
                    @click="toggleField('contactInformation')()"
                    v-if="contactInformation"
                    title="Hide Details"
                  />
                  <font-awesome-icon :icon="['far', 'caret-down']" size="lg"
                    @click="toggleField('contactInformation')()"
                    v-if="!contactInformation"
                    title="View Details"
                  />
                </a>
              </div>
            </div>
          </template>
          <template #es-form>
            <table v-if="contactInformation">
              <tbody>
                <tr>
                  <th class="rule left-padding">
                    eScholar Field
                  </th>
                  <th class="rule">
                    Display Label
                  </th>
                  <th class="rule">
                    Enabled
                  </th>
                  <th class="rule">
                    Required
                  </th>
                </tr>
                <tr :class="{ 'bottom-border': !editCurrentPanel }">
                  <td class="fields has-text-grey">
                    {{ getField('STREET_1').name }}
                  </td>
                  <es-loader v-if="!getField('STREET_1')" class="iconCenter"/>
                  <template v-else>
                    <td>
                      <div v-if="editCurrentPanel">
                        <input
                          class="input"
                          type="text"
                          v-model="getField('STREET_1').customLabel"
                          :title="getField('STREET_1').customLabel"
                          aria-label="Display Label"
                        />
                      </div>
                      <div v-else class="disable-cursor">
                        <input
                          class="input"
                          :class="{ 'is-static': !editCurrentPanel }"
                          :readonly="!editCurrentPanel"
                          :value="getField('STREET_1').customLabel"
                          :title="getField('STREET_1').customLabel"
                          aria-label="Display Label"
                        />
                      </div>
                    </td>
                    <td>
                      <div v-if="editCurrentPanel">
                        <button
                          class="button button--form"
                          :disabled="getField('STREET_1').enabledReadOnly"
                          v-for="(option, index) in booleanOptions"
                          @click="getField('STREET_1').enabled = option.code;
                            validateRequiredButton('STREET_1')"
                          :class="{ 'is-active': getField('STREET_1').enabled === option.code }"
                          :key="index"
                        >
                          {{ option.description }}
                        </button>
                      </div>
                      <div v-else class="disable-cursor">
                        {{ getField('STREET_1').enabled === true ? 'Yes' : 'No' }}
                      </div>
                    </td>
                    <td>
                      <div v-if="editCurrentPanel">
                        <button
                          class="button button--form"
                          :disabled="getField('STREET_1').requiredReadOnly"
                          v-for="(option, index) in booleanOptions"
                          @click="getField('STREET_1').required = option.code;
                            validateRequiredButton('STREET_1', 'NOTENABLED')"
                          :class="{ 'is-active': getField('STREET_1').required === option.code }"
                          :key="index"
                        >
                          {{ option.description }}
                        </button>
                      </div>
                      <div v-else class="disable-cursor">
                        {{ getField('STREET_1').required === true ? 'Yes' : 'No' }}
                      </div>
                    </td>
                  </template>
                </tr>
                <tr :class="{ 'bottom-border': !editCurrentPanel }">
                  <td class="fields has-text-grey">
                    {{ getField('STREET_2').name }}
                  </td>
                  <es-loader v-if="!getField('STREET_2')" class="iconCenter"/>
                  <template v-else>
                    <td>
                      <div v-if="editCurrentPanel">
                        <input
                          class="input"
                          type="text"
                          v-model="getField('STREET_2').customLabel"
                          :title="getField('STREET_2').customLabel"
                          aria-label="Display Label"
                        />
                      </div>
                      <div v-else class="disable-cursor">
                        <input
                          class="input"
                          :class="{ 'is-static': !editCurrentPanel }"
                          :readonly="!editCurrentPanel"
                          :value="getField('STREET_2').customLabel"
                          :title="getField('STREET_2').customLabel"
                          aria-label="Display Label"
                        />
                      </div>
                    </td>
                    <td>
                      <div v-if="editCurrentPanel">
                        <button
                          class="button button--form"
                          :disabled="getField('STREET_2').enabledReadOnly"
                          v-for="(option, index) in booleanOptions"
                          @click="getField('STREET_2').enabled = option.code;
                            validateRequiredButton('STREET_2')"
                          :class="{ 'is-active': getField('STREET_2').enabled === option.code }"
                          :key="index"
                        >
                          {{ option.description }}
                        </button>
                      </div>
                      <div v-else class="disable-cursor">
                        {{ getField('STREET_2').enabled === true ? 'Yes' : 'No' }}
                      </div>
                    </td>
                    <td>
                      <div v-if="editCurrentPanel">
                        <button
                          class="button button--form"
                          :disabled="getField('STREET_2').requiredReadOnly"
                          v-for="(option, index) in booleanOptions"
                          @click="getField('STREET_2').required = option.code;
                            validateRequiredButton('STREET_2', 'NOTENABLED')"
                          :class="{ 'is-active': getField('STREET_2').required === option.code }"
                          :key="index"
                        >
                          {{ option.description }}
                        </button>
                      </div>
                      <div v-else class="disable-cursor">
                        {{ getField('STREET_2').required === true ? 'Yes' : 'No' }}
                      </div>
                    </td>
                  </template>
                </tr>
                <tr :class="{ 'bottom-border': !editCurrentPanel }">
                  <td class="fields has-text-grey">
                    {{ getField('CITY').name }}
                  </td>
                  <es-loader v-if="!getField('CITY')" class="iconCenter"/>
                  <template v-else>
                    <td>
                      <div v-if="editCurrentPanel">
                        <input
                          class="input"
                          type="text"
                          v-model="getField('CITY').customLabel"
                          :title="getField('CITY').customLabel"
                          aria-label="Display Label"
                        />
                      </div>
                      <div v-else class="disable-cursor">
                        <input
                          class="input"
                          :class="{ 'is-static': !editCurrentPanel }"
                          :readonly="!editCurrentPanel"
                          :value="getField('CITY').customLabel"
                          :title="getField('CITY').customLabel"
                          aria-label="Display Label"
                        />
                      </div>
                    </td>
                    <td>
                      <div v-if="editCurrentPanel">
                        <button
                          class="button button--form"
                          :disabled="getField('CITY').enabledReadOnly"
                          v-for="(option, index) in booleanOptions"
                          @click="getField('CITY').enabled = option.code;
                            validateRequiredButton('CITY')"
                          :class="{ 'is-active': getField('CITY').enabled === option.code }"
                          :key="index"
                        >
                          {{ option.description }}
                        </button>
                      </div>
                      <div v-else class="disable-cursor">
                        {{ getField('CITY').enabled === true ? 'Yes' : 'No' }}
                      </div>
                    </td>
                    <td>
                      <div v-if="editCurrentPanel">
                        <button
                          class="button button--form"
                          :disabled="getField('CITY').requiredReadOnly"
                          v-for="(option, index) in booleanOptions"
                          @click="getField('CITY').required = option.code;
                            validateRequiredButton('CITY', 'NOTENABLED')"
                          :class="{ 'is-active': getField('CITY').required === option.code }"
                          :key="index"
                        >
                          {{ option.description }}
                        </button>
                      </div>
                      <div v-else class="disable-cursor">
                        {{ getField('CITY').required === true ? 'Yes' : 'No' }}
                      </div>
                    </td>
                  </template>
                </tr>
                <tr :class="{ 'bottom-border': !editCurrentPanel }">
                  <td class="fields has-text-grey">
                    {{ getField('STATE_CODE').name }}
                  </td>
                  <es-loader v-if="!getField('STATE_CODE')" class="iconCenter"/>
                  <template v-else>
                    <td>
                      <div v-if="editCurrentPanel">
                        <input
                          class="input"
                          type="text"
                          v-model="getField('STATE_CODE').customLabel"
                          :title="getField('STATE_CODE').customLabel"
                          aria-label="Display Label"
                        />
                      </div>
                      <div v-else class="disable-cursor">
                        <input
                          class="input"
                          :class="{ 'is-static': !editCurrentPanel }"
                          :readonly="!editCurrentPanel"
                          :value="getField('STATE_CODE').customLabel"
                          :title="getField('STATE_CODE').customLabel"
                          aria-label="Display Label"
                        />
                      </div>
                    </td>
                    <td>
                      <div v-if="editCurrentPanel">
                        <button
                          class="button button--form"
                          :disabled="getField('STATE_CODE').enabledReadOnly"
                          v-for="(option, index) in booleanOptions"
                          @click="getField('STATE_CODE').enabled = option.code;
                            validateRequiredButton('STATE_CODE')"
                          :class="{ 'is-active': getField('STATE_CODE').enabled === option.code }"
                          :key="index"
                        >
                          {{ option.description }}
                        </button>
                      </div>
                      <div v-else class="disable-cursor">
                        {{ getField('STATE_CODE').enabled === true ? 'Yes' : 'No' }}
                      </div>
                    </td>
                    <td>
                      <div v-if="editCurrentPanel">
                        <button
                          class="button button--form"
                          :disabled="getField('STATE_CODE').requiredReadOnly"
                          v-for="(option, index) in booleanOptions"
                          @click="getField('STATE_CODE').required = option.code;
                            validateRequiredButton('STATE_CODE', 'NOTENABLED')"
                          :class="{ 'is-active': getField('STATE_CODE').required === option.code }"
                          :key="index"
                        >
                          {{ option.description }}
                        </button>
                      </div>
                      <div v-else class="disable-cursor">
                        {{ getField('STATE_CODE').required === true ? 'Yes' : 'No' }}
                      </div>
                    </td>
                  </template>
                </tr>
                <tr :class="{ 'bottom-border': !editCurrentPanel }">
                  <td class="fields has-text-grey">
                    {{ getField('ZIP').name }}
                  </td>
                  <es-loader v-if="!getField('ZIP')" class="iconCenter"/>
                  <template v-else>
                    <td>
                      <div v-if="editCurrentPanel">
                        <input
                          class="input"
                          type="text"
                          v-model="getField('ZIP').customLabel"
                          :title="getField('ZIP').customLabel"
                          aria-label="Display Label"
                        />
                      </div>
                      <div v-else class="disable-cursor">
                        <input
                          class="input"
                          :class="{ 'is-static': !editCurrentPanel }"
                          :readonly="!editCurrentPanel"
                          :value="getField('ZIP').customLabel"
                          :title="getField('ZIP').customLabel"
                          aria-label="Display Label"
                        />
                      </div>
                    </td>
                    <td>
                      <div v-if="editCurrentPanel">
                        <button
                          class="button button--form"
                          :disabled="getField('ZIP').enabledReadOnly"
                          v-for="(option, index) in booleanOptions"
                          @click="getField('ZIP').enabled = option.code;
                            validateRequiredButton('ZIP')"
                          :class="{ 'is-active': getField('ZIP').enabled === option.code }"
                          :key="index"
                        >
                          {{ option.description }}
                        </button>
                      </div>
                      <div v-else class="disable-cursor">
                        {{ getField('ZIP').enabled === true ? 'Yes' : 'No' }}
                      </div>
                    </td>
                    <td>
                      <div v-if="editCurrentPanel">
                        <button
                          class="button button--form"
                          :disabled="getField('ZIP').requiredReadOnly"
                          v-for="(option, index) in booleanOptions"
                          @click="getField('ZIP').required = option.code;
                            validateRequiredButton('ZIP', 'NOTENABLED')"
                          :class="{ 'is-active': getField('ZIP').required === option.code }"
                          :key="index"
                        >
                          {{ option.description }}
                        </button>
                      </div>
                      <div v-else class="disable-cursor">
                        {{ getField('ZIP').required === true ? 'Yes' : 'No' }}
                      </div>
                    </td>
                  </template>
                </tr>
                <tr>
                  <td class="fields has-text-grey">
                    {{ getField('GEOCODE').name }}
                  </td>
                  <es-loader v-if="!getField('GEOCODE')" class="iconCenter"/>
                  <template v-else>
                    <td>
                      <div v-if="editCurrentPanel">
                        <input
                          class="input"
                          type="text"
                          v-model="getField('GEOCODE').customLabel"
                          aria-label="Display Label"
                        />
                      </div>
                      <div v-else class="disable-cursor">
                        <input
                          class="input"
                          :class="{ 'is-static': !editCurrentPanel }"
                          :readonly="!editCurrentPanel"
                          :value="getField('GEOCODE').customLabel"
                          :title="getField('GEOCODE').customLabel"
                          aria-label="Display Label"
                        />
                      </div>
                    </td>
                    <td>
                      <div v-if="editCurrentPanel">
                        <button
                          class="button button--form"
                          :disabled="getField('GEOCODE').enabledReadOnly"
                          v-for="(option, index) in booleanOptions"
                          @click="getField('GEOCODE').enabled = option.code;
                            validateRequiredButton('GEOCODE')"
                          :class="{ 'is-active': getField('GEOCODE').enabled === option.code }"
                          :key="index"
                        >
                          {{ option.description }}
                        </button>
                      </div>
                      <div v-else class="disable-cursor">
                        {{ getField('GEOCODE').enabled === true ? 'Yes' : 'No' }}
                      </div>
                    </td>
                    <td>
                      <div v-if="editCurrentPanel">
                        <button
                          class="button button--form"
                          :disabled="getField('GEOCODE').requiredReadOnly"
                          v-for="(option, index) in booleanOptions"
                          @click="getField('GEOCODE').required = option.code;
                            validateRequiredButton('GEOCODE', 'NOTENABLED')"
                          :class="{ 'is-active': getField('GEOCODE').required === option.code }"
                          :key="index"
                        >
                          {{ option.description }}
                        </button>
                      </div>
                      <div v-else class="disable-cursor">
                        {{ getField('GEOCODE').required === true ? 'Yes' : 'No' }}
                      </div>
                    </td>
                  </template>
                </tr>
              </tbody>
            </table>
          </template>
        </es-form>
        <es-form class="partition-border">
          <template #es-form-header>
            <div class="header-container">
              <div class="title is-6">
                Credential Information
              </div>
              <div class="icon-right">
                <a role="button" tabindex="0"
                  @keyup.enter="toggleField('credentialInformation')()">
                  <font-awesome-icon :icon="['far', 'caret-up']" size="lg"
                    @click="toggleField('credentialInformation')()"
                    v-if="credentialInformation"
                    title="Hide Details"
                  />
                  <font-awesome-icon :icon="['far', 'caret-down']" size="lg"
                    @click="toggleField('credentialInformation')()"
                    v-if="!credentialInformation"
                    title="View Details"
                  />
                </a>
              </div>
            </div>
          </template>
          <template #es-form>
            <table v-if="credentialInformation">
              <tbody>
                <tr>
                  <th class="rule left-padding">
                    eScholar Field
                  </th>
                  <th class="rule">
                    Display Label
                  </th>
                  <th class="rule">
                    Enabled
                  </th>
                  <th class="rule">
                    Required
                  </th>
                </tr>
                <tr :class="{ 'bottom-border': !editCurrentPanel }">
                  <td class="fields has-text-grey">
                    {{ getField('HIGHEST_DEGREE').name }}
                  </td>
                  <es-loader v-if="!getField('HIGHEST_DEGREE')" class="iconCenter"/>
                  <template v-else>
                    <td>
                      <div v-if="editCurrentPanel">
                        <input
                          class="input"
                          type="text"
                          v-model="getField('HIGHEST_DEGREE').customLabel"
                          :title="getField('HIGHEST_DEGREE').customLabel"
                          aria-label="Display Label"
                        />
                      </div>
                      <div v-else class="disable-cursor">
                        <input
                          class="input"
                          :class="{ 'is-static': !editCurrentPanel }"
                          :readonly="!editCurrentPanel"
                          :value="getField('HIGHEST_DEGREE').customLabel"
                          :title="getField('HIGHEST_DEGREE').customLabel"
                          aria-label="Display Label"
                        />
                      </div>
                    </td>
                    <td>
                      <div v-if="editCurrentPanel">
                        <button
                          class="button button--form"
                          :disabled="getField('HIGHEST_DEGREE').enabledReadOnly"
                          v-for="(option, index) in booleanOptions"
                          @click="getField('HIGHEST_DEGREE').enabled = option.code;
                            validateRequiredButton('HIGHEST_DEGREE')"
                          :class="{ 'is-active': getField('HIGHEST_DEGREE')
                            .enabled === option.code }"
                          :key="index"
                        >
                          {{ option.description }}
                        </button>
                      </div>
                      <div v-else class="disable-cursor">
                        {{ getField('HIGHEST_DEGREE').enabled === true ? 'Yes' : 'No' }}
                      </div>
                    </td>
                    <td>
                      <div v-if="editCurrentPanel">
                        <button
                          class="button button--form"
                          :disabled="getField('HIGHEST_DEGREE').requiredReadOnly"
                          v-for="(option, index) in booleanOptions"
                          @click="getField('HIGHEST_DEGREE').required = option.code;
                            validateRequiredButton('HIGHEST_DEGREE', 'NOTENABLED')"
                          :class="{ 'is-active': getField('HIGHEST_DEGREE')
                            .required === option.code }"
                          :key="index"
                        >
                          {{ option.description }}
                        </button>
                      </div>
                      <div v-else class="disable-cursor">
                        {{ getField('HIGHEST_DEGREE').required === true ? 'Yes' : 'No' }}
                      </div>
                    </td>
                  </template>
                </tr>
                <tr :class="{ 'bottom-border': !editCurrentPanel }">
                  <td class="fields has-text-grey">
                    {{ getField('SUBJECT_MATTER_AREA').name }}
                  </td>
                  <es-loader v-if="!getField('SUBJECT_MATTER_AREA')" class="iconCenter"/>
                  <template v-else>
                    <td>
                      <div v-if="editCurrentPanel">
                        <input
                          class="input"
                          type="text"
                          v-model="getField('SUBJECT_MATTER_AREA').customLabel"
                          :title="getField('SUBJECT_MATTER_AREA').customLabel"
                          aria-label="Display Label"
                        />
                      </div>
                      <div v-else class="disable-cursor">
                        <input
                          class="input"
                          :class="{ 'is-static': !editCurrentPanel }"
                          :readonly="!editCurrentPanel"
                          :value="getField('SUBJECT_MATTER_AREA').customLabel"
                          :title="getField('SUBJECT_MATTER_AREA').customLabel"
                          aria-label="Display Label"
                        />
                      </div>
                    </td>
                    <td>
                      <div v-if="editCurrentPanel">
                        <button
                          class="button button--form"
                          :disabled="getField('SUBJECT_MATTER_AREA').enabledReadOnly"
                          v-for="(option, index) in booleanOptions"
                          @click="getField('SUBJECT_MATTER_AREA').enabled = option.code;
                            validateRequiredButton('SUBJECT_MATTER_AREA')"
                          :class="{ 'is-active': getField('SUBJECT_MATTER_AREA')
                            .enabled === option.code }"
                          :key="index"
                        >
                          {{ option.description }}
                        </button>
                      </div>
                      <div v-else class="disable-cursor">
                        {{ getField('SUBJECT_MATTER_AREA').enabled === true ? 'Yes' : 'No' }}
                      </div>
                    </td>
                    <td>
                      <div v-if="editCurrentPanel">
                        <button
                          class="button button--form"
                          :disabled="getField('SUBJECT_MATTER_AREA').requiredReadOnly"
                          v-for="(option, index) in booleanOptions"
                          @click="getField('SUBJECT_MATTER_AREA').required = option.code;
                            validateRequiredButton('SUBJECT_MATTER_AREA', 'NOTENABLED')"
                          :class="{ 'is-active': getField('SUBJECT_MATTER_AREA')
                            .required === option.code }"
                          :key="index"
                        >
                          {{ option.description }}
                        </button>
                      </div>
                      <div v-else class="disable-cursor">
                        {{ getField('SUBJECT_MATTER_AREA').required === true ? 'Yes' : 'No' }}
                      </div>
                    </td>
                  </template>
                </tr>
                <tr :class="{ 'bottom-border': !editCurrentPanel }">
                  <td class="fields has-text-grey">
                    {{ getField('DEGREE_PROVIDER').name }}
                  </td>
                  <es-loader v-if="!getField('DEGREE_PROVIDER')" class="iconCenter"/>
                  <template v-else>
                    <td>
                      <div v-if="editCurrentPanel">
                        <input
                          class="input"
                          type="text"
                          v-model="getField('DEGREE_PROVIDER').customLabel"
                          :title="getField('DEGREE_PROVIDER').customLabel"
                          aria-label="Display Label"
                        />
                      </div>
                      <div v-else class="disable-cursor">
                        <input
                          class="input"
                          :class="{ 'is-static': !editCurrentPanel }"
                          :readonly="!editCurrentPanel"
                          :value="getField('DEGREE_PROVIDER').customLabel"
                          :title="getField('DEGREE_PROVIDER').customLabel"
                          aria-label="Display Label"
                        />
                      </div>
                    </td>
                    <td>
                      <div v-if="editCurrentPanel">
                        <button
                          class="button button--form"
                          :disabled="getField('DEGREE_PROVIDER').enabledReadOnly"
                          v-for="(option, index) in booleanOptions"
                          @click="getField('DEGREE_PROVIDER').enabled = option.code;
                            validateRequiredButton('DEGREE_PROVIDER')"
                          :class="{ 'is-active': getField('DEGREE_PROVIDER')
                            .enabled === option.code }"
                          :key="index"
                        >
                          {{ option.description }}
                        </button>
                      </div>
                      <div v-else class="disable-cursor">
                        {{ getField('DEGREE_PROVIDER').enabled === true ? 'Yes' : 'No' }}
                      </div>
                    </td>
                    <td>
                      <div v-if="editCurrentPanel">
                        <button
                          class="button button--form"
                          :disabled="getField('DEGREE_PROVIDER').requiredReadOnly"
                          v-for="(option, index) in booleanOptions"
                          @click="getField('DEGREE_PROVIDER').required = option.code;
                            validateRequiredButton('DEGREE_PROVIDER', 'NOTENABLED')"
                          :class="{ 'is-active': getField('DEGREE_PROVIDER')
                            .required === option.code }"
                          :key="index"
                        >
                          {{ option.description }}
                        </button>
                      </div>
                      <div v-else class="disable-cursor">
                        {{ getField('DEGREE_PROVIDER').required === true ? 'Yes' : 'No' }}
                      </div>
                    </td>
                  </template>
                </tr>
                <tr :class="{ 'bottom-border': !editCurrentPanel }">
                  <td class="fields has-text-grey">
                    {{ getField('CERTIFICATE_NUMBER').name }}
                  </td>
                  <es-loader v-if="!getField('CERTIFICATE_NUMBER')" class="iconCenter"/>
                  <template v-else>
                    <td>
                      <div v-if="editCurrentPanel">
                        <input
                          class="input"
                          type="text"
                          v-model="getField('CERTIFICATE_NUMBER').customLabel"
                          :title="getField('CERTIFICATE_NUMBER').customLabel"
                          aria-label="Display Label"
                        />
                      </div>
                      <div v-else class="disable-cursor">
                        <input
                          class="input"
                          :class="{ 'is-static': !editCurrentPanel }"
                          :readonly="!editCurrentPanel"
                          :value="getField('CERTIFICATE_NUMBER').customLabel"
                          :title="getField('CERTIFICATE_NUMBER').customLabel"
                          aria-label="Display Label"
                        />
                      </div>
                    </td>
                    <td>
                      <div v-if="editCurrentPanel">
                        <button
                          class="button button--form"
                          :disabled="getField('CERTIFICATE_NUMBER').enabledReadOnly"
                          v-for="(option, index) in booleanOptions"
                          @click="getField('CERTIFICATE_NUMBER').enabled = option.code;
                            validateRequiredButton('CERTIFICATE_NUMBER')"
                          :class="{ 'is-active': getField('CERTIFICATE_NUMBER')
                            .enabled === option.code }"
                          :key="index"
                        >
                          {{ option.description }}
                        </button>
                      </div>
                      <div v-else class="disable-cursor">
                        {{ getField('CERTIFICATE_NUMBER').enabled === true ? 'Yes' : 'No' }}
                      </div>
                    </td>
                    <td>
                      <div v-if="editCurrentPanel">
                        <button
                          class="button button--form"
                          :disabled="getField('CERTIFICATE_NUMBER').requiredReadOnly"
                          v-for="(option, index) in booleanOptions"
                          @click="getField('CERTIFICATE_NUMBER').required = option.code;
                            validateRequiredButton('CERTIFICATE_NUMBER', 'NOTENABLED')"
                          :class="{ 'is-active': getField('CERTIFICATE_NUMBER')
                            .required === option.code }"
                          :key="index"
                        >
                          {{ option.description }}
                        </button>
                      </div>
                      <div v-else class="disable-cursor">
                        {{ getField('CERTIFICATE_NUMBER').required === true ? 'Yes' : 'No' }}
                      </div>
                    </td>
                  </template>
                </tr>
                <tr>
                  <td class="fields has-text-grey">
                    {{ getField('CERTIFICATE_TYPE').name }}
                  </td>
                  <es-loader v-if="!getField('CERTIFICATE_TYPE')" class="iconCenter"/>
                  <template v-else>
                    <td>
                      <div v-if="editCurrentPanel">
                        <input
                          class="input"
                          type="text"
                          v-model="getField('CERTIFICATE_TYPE').customLabel"
                          :title="getField('CERTIFICATE_TYPE').customLabel"
                          aria-label="Display Label"
                        />
                      </div>
                      <div v-else class="disable-cursor">
                        <input
                          class="input"
                          :class="{ 'is-static': !editCurrentPanel }"
                          :readonly="!editCurrentPanel"
                          :value="getField('CERTIFICATE_TYPE').customLabel"
                          :title="getField('CERTIFICATE_TYPE').customLabel"
                          aria-label="Display Label"
                        />
                      </div>
                    </td>
                    <td>
                      <div v-if="editCurrentPanel">
                        <button
                          class="button button--form"
                          :disabled="getField('CERTIFICATE_TYPE').enabledReadOnly"
                          v-for="(option, index) in booleanOptions"
                          @click="getField('CERTIFICATE_TYPE').enabled = option.code;
                            validateRequiredButton('CERTIFICATE_TYPE')"
                          :class="{ 'is-active': getField('CERTIFICATE_TYPE')
                            .enabled === option.code }"
                          :key="index"
                        >
                          {{ option.description }}
                        </button>
                      </div>
                      <div v-else class="disable-cursor">
                        {{ getField('CERTIFICATE_TYPE').enabled === true ? 'Yes' : 'No' }}
                      </div>
                    </td>
                    <td>
                      <div v-if="editCurrentPanel">
                        <button
                          class="button button--form"
                          :disabled="getField('CERTIFICATE_TYPE').requiredReadOnly"
                          v-for="(option, index) in booleanOptions"
                          @click="getField('CERTIFICATE_TYPE').required = option.code;
                            validateRequiredButton('CERTIFICATE_TYPE', 'NOTENABLED')"
                          :class="{ 'is-active': getField('CERTIFICATE_TYPE')
                            .required === option.code }"
                          :key="index"
                        >
                          {{ option.description }}
                        </button>
                      </div>
                      <div v-else class="disable-cursor">
                        {{ getField('CERTIFICATE_TYPE').required === true ? 'Yes' : 'No' }}
                      </div>
                    </td>
                  </template>
                </tr>
              </tbody>
            </table>
          </template>
        </es-form>
        <es-form class="partition-border">
          <template #es-form-header>
            <div class="header-container">
              <div class="title is-6">
                Other Information
              </div>
              <div class="icon-right">
                <a role="button" tabindex="0"
                  @keyup.enter="toggleField('otherInformation')()">
                  <font-awesome-icon :icon="['far', 'caret-up']" size="lg"
                    @click="toggleField('otherInformation')()"
                    v-if="otherInformation"
                    title="View Details"
                  />
                  <font-awesome-icon :icon="['far', 'caret-down']" size="lg"
                    @click="toggleField('otherInformation')()"
                    v-if="!otherInformation"
                    title="View Details"
                  />
                </a>
              </div>
            </div>
          </template>
          <template #es-form>
            <table v-if="otherInformation">
              <tbody>
                <tr>
                  <th class="rule left-padding">
                    eScholar Field
                  </th>
                  <th class="rule">
                    Display Label
                  </th>
                  <th class="rule">
                    Enabled
                  </th>
                  <th class="rule">
                    Required
                  </th>
                </tr>
                <tr :class="{ 'bottom-border': !editCurrentPanel }">
                  <td class="fields has-text-grey">
                    {{ getField('BIRTH_LOCATION').name }}
                  </td>
                  <es-loader v-if="!getField('BIRTH_LOCATION')" class="iconCenter"/>
                  <template v-else>
                    <td>
                      <div v-if="editCurrentPanel">
                        <input
                          class="input"
                          type="text"
                          v-model="getField('BIRTH_LOCATION').customLabel"
                          :title="getField('BIRTH_LOCATION').customLabel"
                          aria-label="Display Label"
                        />
                      </div>
                      <div v-else class="disable-cursor">
                        <input
                          class="input"
                          :class="{ 'is-static': !editCurrentPanel }"
                          :readonly="!editCurrentPanel"
                          :value="getField('BIRTH_LOCATION').customLabel"
                          :title="getField('BIRTH_LOCATION').customLabel"
                          aria-label="Display Label"
                        />
                      </div>
                    </td>
                    <td>
                      <div v-if="editCurrentPanel">
                        <button
                          class="button button--form"
                          :disabled="getField('BIRTH_LOCATION').enabledReadOnly"
                          v-for="(option, index) in booleanOptions"
                          @click="getField('BIRTH_LOCATION').enabled = option.code;
                            validateRequiredButton('BIRTH_LOCATION')"
                          :class="{ 'is-active': getField('BIRTH_LOCATION')
                            .enabled === option.code }"
                          :key="index"
                        >
                          {{ option.description }}
                        </button>
                      </div>
                      <div v-else class="disable-cursor">
                        {{ getField('BIRTH_LOCATION').enabled === true ? 'Yes' : 'No' }}
                      </div>
                    </td>
                    <td>
                      <div v-if="editCurrentPanel">
                        <button
                          class="button button--form"
                          :disabled="getField('BIRTH_LOCATION').requiredReadOnly"
                          v-for="(option, index) in booleanOptions"
                          @click="getField('BIRTH_LOCATION').required = option.code;
                            validateRequiredButton('BIRTH_LOCATION', 'NOTENABLED')"
                          :class="{ 'is-active': getField('BIRTH_LOCATION')
                            .required === option.code }"
                          :key="index"
                        >
                          {{ option.description }}
                        </button>
                      </div>
                      <div v-else class="disable-cursor">
                        {{ getField('BIRTH_LOCATION').required === true ? 'Yes' : 'No' }}
                      </div>
                    </td>
                  </template>
                </tr>
                <tr :class="{ 'bottom-border': !editCurrentPanel }">
                  <td class="fields has-text-grey">
                    {{ getField('RESIDENCY_STATUS').name }}
                  </td>
                  <es-loader v-if="!getField('RESIDENCY_STATUS')" class="iconCenter"/>
                  <template v-else>
                    <td>
                      <div v-if="editCurrentPanel">
                        <input
                          class="input"
                          type="text"
                          v-model="getField('RESIDENCY_STATUS').customLabel"
                          :title="getField('RESIDENCY_STATUS').customLabel"
                          aria-label="Display Label"
                        />
                      </div>
                      <div v-else class="disable-cursor">
                        <input
                          class="input"
                          :class="{ 'is-static': !editCurrentPanel }"
                          :readonly="!editCurrentPanel"
                          :value="getField('RESIDENCY_STATUS').customLabel"
                          :title="getField('RESIDENCY_STATUS').customLabel"
                          aria-label="Display Label"
                        />
                      </div>
                    </td>
                    <td>
                      <div v-if="editCurrentPanel">
                        <button
                          class="button button--form"
                          :disabled="getField('RESIDENCY_STATUS').enabledReadOnly"
                          v-for="(option, index) in booleanOptions"
                          @click="getField('RESIDENCY_STATUS').enabled = option.code;
                            validateRequiredButton('RESIDENCY_STATUS')"
                          :class="{ 'is-active': getField('RESIDENCY_STATUS')
                            .enabled === option.code }"
                          :key="index"
                        >
                          {{ option.description }}
                        </button>
                      </div>
                      <div v-else class="disable-cursor">
                        {{ getField('RESIDENCY_STATUS').enabled === true ? 'Yes' : 'No' }}
                      </div>
                    </td>
                    <td>
                      <div v-if="editCurrentPanel">
                        <button
                          class="button button--form"
                          :disabled="getField('RESIDENCY_STATUS').requiredReadOnly"
                          v-for="(option, index) in booleanOptions"
                          @click="getField('RESIDENCY_STATUS').required = option.code;
                            validateRequiredButton('RESIDENCY_STATUS', 'NOTENABLED')"
                          :class="{ 'is-active': getField('RESIDENCY_STATUS')
                            .required === option.code }"
                          :key="index"
                        >
                          {{ option.description }}
                        </button>
                      </div>
                      <div v-else class="disable-cursor">
                        {{ getField('RESIDENCY_STATUS').required === true ? 'Yes' : 'No' }}
                      </div>
                    </td>
                  </template>
                </tr>
                <tr :class="{ 'bottom-border': !editCurrentPanel }">
                  <td class="fields has-text-grey">
                    {{ getField('RESIDENCY_EXPIRATION_DATE').name }}
                  </td>
                  <es-loader v-if="!getField('RESIDENCY_EXPIRATION_DATE')" class="iconCenter"/>
                  <template v-else>
                    <td>
                      <div v-if="editCurrentPanel">
                        <input
                          class="input"
                          type="text"
                          v-model="getField('RESIDENCY_EXPIRATION_DATE').customLabel"
                          :title="getField('RESIDENCY_EXPIRATION_DATE').customLabel"
                          aria-label="Display Label"
                        />
                      </div>
                      <div v-else class="disable-cursor">
                        <input
                          class="input"
                          :class="{ 'is-static': !editCurrentPanel }"
                          :readonly="!editCurrentPanel"
                          :value="getField('RESIDENCY_EXPIRATION_DATE').customLabel"
                          :title="getField('RESIDENCY_EXPIRATION_DATE').customLabel"
                          aria-label="Display Label"
                        />
                      </div>
                    </td>
                    <td>
                      <div v-if="editCurrentPanel">
                        <button
                          class="button button--form"
                          :disabled="getField('RESIDENCY_EXPIRATION_DATE').enabledReadOnly"
                          v-for="(option, index) in booleanOptions"
                          @click="getField('RESIDENCY_EXPIRATION_DATE').enabled = option.code;
                            validateRequiredButton('RESIDENCY_EXPIRATION_DATE')"
                          :class="{ 'is-active': getField('RESIDENCY_EXPIRATION_DATE')
                            .enabled === option.code }"
                          :key="index"
                        >
                          {{ option.description }}
                        </button>
                      </div>
                      <div v-else class="disable-cursor">
                        {{ getField('RESIDENCY_EXPIRATION_DATE').enabled === true ? 'Yes' : 'No' }}
                      </div>
                    </td>
                    <td>
                      <div v-if="editCurrentPanel">
                        <button
                          class="button button--form"
                          :disabled="getField('RESIDENCY_EXPIRATION_DATE').requiredReadOnly"
                          v-for="(option, index) in booleanOptions"
                          @click="getField('RESIDENCY_EXPIRATION_DATE').required = option.code;
                            validateRequiredButton('RESIDENCY_EXPIRATION_DATE', 'NOTENABLED')"
                          :class="{ 'is-active': getField('RESIDENCY_EXPIRATION_DATE')
                            .required === option.code }"
                          :key="index"
                        >
                          {{ option.description }}
                        </button>
                      </div>
                      <div v-else class="disable-cursor">
                        {{ getField('RESIDENCY_EXPIRATION_DATE').required === true ? 'Yes' : 'No' }}
                      </div>
                    </td>
                  </template>
                </tr>
                <tr :class="{ 'bottom-border': !editCurrentPanel }">
                  <td class="fields has-text-grey">
                    {{ getField('MOTHERS_MAIDEN_NAME').name }}
                  </td>
                  <es-loader v-if="!getField('MOTHERS_MAIDEN_NAME')" class="iconCenter"/>
                  <template v-else>
                    <td>
                      <div v-if="editCurrentPanel">
                        <input
                          class="input"
                          type="text"
                          v-model="getField('MOTHERS_MAIDEN_NAME').customLabel"
                          :title="getField('MOTHERS_MAIDEN_NAME').customLabel"
                          aria-label="Display Label"
                        />
                      </div>
                      <div v-else class="disable-cursor">
                        <input
                          class="input"
                          :class="{ 'is-static': !editCurrentPanel }"
                          :readonly="!editCurrentPanel"
                          :value="getField('MOTHERS_MAIDEN_NAME').customLabel"
                          :title="getField('MOTHERS_MAIDEN_NAME').customLabel"
                          aria-label="Display Label"
                        />
                      </div>
                    </td>
                    <td>
                      <div v-if="editCurrentPanel">
                        <button
                          class="button button--form"
                          :disabled="getField('MOTHERS_MAIDEN_NAME').enabledReadOnly"
                          v-for="(option, index) in booleanOptions"
                          @click="getField('MOTHERS_MAIDEN_NAME').enabled = option.code;
                            validateRequiredButton('MOTHERS_MAIDEN_NAME')"
                          :class="{ 'is-active': getField('MOTHERS_MAIDEN_NAME')
                            .enabled === option.code }"
                          :key="index"
                        >
                          {{ option.description }}
                        </button>
                      </div>
                      <div v-else class="disable-cursor">
                        {{ getField('MOTHERS_MAIDEN_NAME').enabled === true ? 'Yes' : 'No' }}
                      </div>
                    </td>
                    <td>
                      <div v-if="editCurrentPanel">
                        <button
                          class="button button--form"
                          :disabled="getField('MOTHERS_MAIDEN_NAME').requiredReadOnly"
                          v-for="(option, index) in booleanOptions"
                          @click="getField('MOTHERS_MAIDEN_NAME').required = option.code;
                            validateRequiredButton('MOTHERS_MAIDEN_NAME', 'NOTENABLED')"
                          :class="{ 'is-active': getField('MOTHERS_MAIDEN_NAME')
                            .required === option.code }"
                          :key="index"
                        >
                          {{ option.description }}
                        </button>
                      </div>
                      <div v-else class="disable-cursor">
                        {{ getField('MOTHERS_MAIDEN_NAME').required === true ? 'Yes' : 'No' }}
                      </div>
                    </td>
                  </template>
                </tr>
              </tbody>
            </table>
          </template>
        </es-form>
        <es-form class="partition-border">
          <template #es-form-header>
            <div class="header-container">
              <div class="title is-6">
                Customer Defined Fields
              </div>
              <div class="icon-right">
                <a role="button" tabindex="0"
                  @keyup.enter="toggleField('cdfInformation')()">
                  <font-awesome-icon :icon="['far', 'caret-up']" size="lg"
                    @click="toggleField('cdfInformation')()"
                    v-if="cdfInformation"
                    title="Hide Details"
                  />
                  <font-awesome-icon :icon="['far', 'caret-down']" size="lg"
                    @click="toggleField('cdfInformation')()"
                    v-if="!cdfInformation"
                    title="View Details"
                  />
                </a>
              </div>
            </div>
          </template>
          <template #es-form>
            <table v-if="cdfInformation">
              <tbody>
                <tr>
                  <th class="rule left-padding">
                    eScholar Field
                  </th>
                  <th class="rule">
                    Display Label
                  </th>
                  <th class="rule">
                    Enabled
                  </th>
                  <th class="rule">
                    Required
                  </th>
                </tr>
                <tr :class="{ 'bottom-border': !editCurrentPanel }">
                  <td class="fields has-text-grey">
                    {{ getField('cdf_1').name }}
                  </td>
                  <es-loader v-if="!getField('cdf_1')" class="iconCenter"/>
                  <template v-else>
                    <td>
                      <div v-if="editCurrentPanel">
                        <input
                          class="input"
                          type="text"
                          v-model="getField('cdf_1').customLabel"
                          :title="getField('cdf_1').customLabel"
                          aria-label="Display Label"
                        />
                      </div>
                      <div v-else class="disable-cursor">
                        <input
                          class="input"
                          :class="{ 'is-static': !editCurrentPanel }"
                          :readonly="!editCurrentPanel"
                          :value="getField('cdf_1').customLabel"
                          :title="getField('cdf_1').customLabel"
                          aria-label="Display Label"
                        />
                      </div>
                    </td>
                    <td>
                      <div v-if="editCurrentPanel">
                        <button
                          class="button button--form"
                          :disabled="getField('cdf_1').enabledReadOnly"
                          v-for="(option, index) in booleanOptions"
                          @click="getField('cdf_1').enabled = option.code;
                            validateRequiredButton('cdf_1')"
                          :class="{ 'is-active': getField('cdf_1').enabled === option.code }"
                          :key="index"
                        >
                          {{ option.description }}
                        </button>
                      </div>
                      <div v-else class="disable-cursor">
                        {{ getField('cdf_1').enabled === true ? 'Yes' : 'No' }}
                      </div>
                    </td>
                    <td>
                      <div v-if="editCurrentPanel">
                        <button
                          class="button button--form"
                          :disabled="getField('cdf_1').requiredReadOnly"
                          v-for="(option, index) in booleanOptions"
                          @click="getField('cdf_1').required = option.code;
                            validateRequiredButton('cdf_1', 'NOTENABLED')"
                          :class="{ 'is-active': getField('cdf_1').required === option.code }"
                          :key="index"
                        >
                          {{ option.description }}
                        </button>
                      </div>
                      <div v-else class="disable-cursor">
                        {{ getField('cdf_1').required === true ? 'Yes' : 'No' }}
                      </div>
                    </td>
                  </template>
                </tr>
                <tr :class="{ 'bottom-border': !editCurrentPanel }">
                  <td class="fields has-text-grey">
                    {{ getField('cdf_2').name }}
                  </td>
                  <es-loader v-if="!getField('cdf_2')" class="iconCenter"/>
                  <template v-else>
                    <td>
                      <div v-if="editCurrentPanel">
                        <input
                          class="input"
                          type="text"
                          v-model="getField('cdf_2').customLabel"
                          :title="getField('cdf_2').customLabel"
                          aria-label="Display Label"
                        />
                      </div>
                      <div v-else class="disable-cursor">
                        <input
                          class="input"
                          :class="{ 'is-static': !editCurrentPanel }"
                          :readonly="!editCurrentPanel"
                          :value="getField('cdf_2').customLabel"
                          :title="getField('cdf_2').customLabel"
                          aria-label="Display Label"
                        />
                      </div>
                    </td>
                    <td>
                      <div v-if="editCurrentPanel">
                        <button
                          class="button button--form"
                          :disabled="getField('cdf_2').enabledReadOnly"
                          v-for="(option, index) in booleanOptions"
                          @click="getField('cdf_2').enabled = option.code;
                            validateRequiredButton('cdf_2')"
                          :class="{ 'is-active': getField('cdf_2').enabled === option.code }"
                          :key="index"
                        >
                          {{ option.description }}
                        </button>
                      </div>
                      <div v-else class="disable-cursor">
                        {{ getField('cdf_2').enabled === true ? 'Yes' : 'No' }}
                      </div>
                    </td>
                    <td>
                      <div v-if="editCurrentPanel">
                        <button
                          class="button button--form"
                          :disabled="getField('cdf_2').requiredReadOnly"
                          v-for="(option, index) in booleanOptions"
                          @click="getField('cdf_2').required = option.code;
                            validateRequiredButton('cdf_2', 'NOTENABLED')"
                          :class="{ 'is-active': getField('cdf_2').required === option.code }"
                          :key="index"
                        >
                          {{ option.description }}
                        </button>
                      </div>
                      <div v-else class="disable-cursor">
                        {{ getField('cdf_2').required === true ? 'Yes' : 'No' }}
                      </div>
                    </td>
                  </template>
                </tr>
                <tr :class="{ 'bottom-border': !editCurrentPanel }">
                  <td class="fields has-text-grey">
                    {{ getField('cdf_3').name }}
                  </td>
                  <es-loader v-if="!getField('cdf_3')" class="iconCenter"/>
                  <template v-else>
                    <td>
                      <div v-if="editCurrentPanel">
                        <input
                          class="input"
                          type="text"
                          v-model="getField('cdf_3').customLabel"
                          :title="getField('cdf_3').customLabel"
                          aria-label="Display Label"
                        />
                      </div>
                      <div v-else class="disable-cursor">
                        <input
                          class="input"
                          :class="{ 'is-static': !editCurrentPanel }"
                          :readonly="!editCurrentPanel"
                          :value="getField('cdf_3').customLabel"
                          :title="getField('cdf_3').customLabel"
                          aria-label="Display Label"
                        />
                      </div>
                    </td>
                    <td>
                      <div v-if="editCurrentPanel">
                        <button
                          class="button button--form"
                          :disabled="getField('cdf_3').enabledReadOnly"
                          v-for="(option, index) in booleanOptions"
                          @click="getField('cdf_3').enabled = option.code;
                            validateRequiredButton('cdf_3')"
                          :class="{ 'is-active': getField('cdf_3').enabled === option.code }"
                          :key="index"
                        >
                          {{ option.description }}
                        </button>
                      </div>
                      <div v-else class="disable-cursor">
                        {{ getField('cdf_3').enabled === true ? 'Yes' : 'No' }}
                      </div>
                    </td>
                    <td>
                      <div v-if="editCurrentPanel">
                        <button
                          class="button button--form"
                          :disabled="getField('cdf_3').requiredReadOnly"
                          v-for="(option, index) in booleanOptions"
                          @click="getField('cdf_3').required = option.code;
                            validateRequiredButton('cdf_3', 'NOTENABLED')"
                          :class="{ 'is-active': getField('cdf_3').required === option.code }"
                          :key="index"
                        >
                          {{ option.description }}
                        </button>
                      </div>
                      <div v-else class="disable-cursor">
                        {{ getField('cdf_3').required === true ? 'Yes' : 'No' }}
                      </div>
                    </td>
                  </template>
                </tr>
                <tr :class="{ 'bottom-border': !editCurrentPanel }">
                  <td class="fields has-text-grey">
                    {{ getField('cdf_4').name }}
                  </td>
                  <es-loader v-if="!getField('cdf_4')" class="iconCenter"/>
                  <template v-else>
                    <td>
                      <div v-if="editCurrentPanel">
                        <input
                          class="input"
                          type="text"
                          v-model="getField('cdf_4').customLabel"
                          :title="getField('cdf_4').customLabel"
                          aria-label="Display Label"
                        />
                      </div>
                      <div v-else class="disable-cursor">
                        <input
                          class="input"
                          :class="{ 'is-static': !editCurrentPanel }"
                          :readonly="!editCurrentPanel"
                          :value="getField('cdf_4').customLabel"
                          :title="getField('cdf_4').customLabel"
                          aria-label="Display Label"
                        />
                      </div>
                    </td>
                    <td>
                      <div v-if="editCurrentPanel">
                        <button
                          class="button button--form"
                          :disabled="getField('cdf_4').enabledReadOnly"
                          v-for="(option, index) in booleanOptions"
                          @click="getField('cdf_4').enabled = option.code;
                            validateRequiredButton('cdf_4')"
                          :class="{ 'is-active': getField('cdf_4').enabled === option.code }"
                          :key="index"
                        >
                          {{ option.description }}
                        </button>
                      </div>
                      <div v-else class="disable-cursor">
                        {{ getField('cdf_4').enabled === true ? 'Yes' : 'No' }}
                      </div>
                    </td>
                    <td>
                      <div v-if="editCurrentPanel">
                        <button
                          class="button button--form"
                          :disabled="getField('cdf_4').requiredReadOnly"
                          v-for="(option, index) in booleanOptions"
                          @click="getField('cdf_4').required = option.code;
                            validateRequiredButton('cdf_4', 'NOTENABLED')"
                          :class="{ 'is-active': getField('cdf_4').required === option.code }"
                          :key="index"
                        >
                          {{ option.description }}
                        </button>
                      </div>
                      <div v-else class="disable-cursor">
                        {{ getField('cdf_4').required === true ? 'Yes' : 'No' }}
                      </div>
                    </td>
                  </template>
                </tr>
                <tr :class="{ 'bottom-border': !editCurrentPanel }">
                  <td class="fields has-text-grey">
                    {{ getField('cdf_5').name }}
                  </td>
                  <es-loader v-if="!getField('cdf_5')" class="iconCenter"/>
                  <template v-else>
                    <td>
                      <div v-if="editCurrentPanel">
                        <input
                          class="input"
                          type="text"
                          v-model="getField('cdf_5').customLabel"
                          :title="getField('cdf_5').customLabel"
                          aria-label="Display Label"
                        />
                      </div>
                      <div v-else class="disable-cursor">
                        <input
                          class="input"
                          :class="{ 'is-static': !editCurrentPanel }"
                          :readonly="!editCurrentPanel"
                          :value="getField('cdf_5').customLabel"
                          :title="getField('cdf_5').customLabel"
                          aria-label="Display Label"
                        />
                      </div>
                    </td>
                    <td>
                      <div v-if="editCurrentPanel">
                        <button
                          class="button button--form"
                          :disabled="getField('cdf_5').enabledReadOnly"
                          v-for="(option, index) in booleanOptions"
                          @click="getField('cdf_5').enabled = option.code;
                            validateRequiredButton('cdf_5')"
                          :class="{ 'is-active': getField('cdf_5').enabled === option.code }"
                          :key="index"
                        >
                          {{ option.description }}
                        </button>
                      </div>
                      <div v-else class="disable-cursor">
                        {{ getField('cdf_5').enabled === true ? 'Yes' : 'No' }}
                      </div>
                    </td>
                    <td>
                      <div v-if="editCurrentPanel">
                        <button
                          class="button button--form"
                          :disabled="getField('cdf_5').requiredReadOnly"
                          v-for="(option, index) in booleanOptions"
                          @click="getField('cdf_5').required = option.code;
                            validateRequiredButton('cdf_5', 'NOTENABLED')"
                          :class="{ 'is-active': getField('cdf_5').required === option.code }"
                          :key="index"
                        >
                          {{ option.description }}
                        </button>
                      </div>
                      <div v-else class="disable-cursor">
                        {{ getField('cdf_5').required === true ? 'Yes' : 'No' }}
                      </div>
                    </td>
                  </template>
                </tr>
                <tr :class="{ 'bottom-border': !editCurrentPanel }">
                  <td class="fields has-text-grey">
                    {{ getField('cdf_6').name }}
                  </td>
                  <es-loader v-if="!getField('cdf_6')" class="iconCenter"/>
                  <template v-else>
                    <td>
                      <div v-if="editCurrentPanel">
                        <input
                          class="input"
                          type="text"
                          v-model="getField('cdf_6').customLabel"
                          :title="getField('cdf_6').customLabel"
                          aria-label="Display Label"
                        />
                      </div>
                      <div v-else class="disable-cursor">
                        <input
                          class="input"
                          :class="{ 'is-static': !editCurrentPanel }"
                          :readonly="!editCurrentPanel"
                          :value="getField('cdf_6').customLabel"
                          :title="getField('cdf_6').customLabel"
                          aria-label="Display Label"
                        />
                      </div>
                    </td>
                    <td>
                      <div v-if="editCurrentPanel">
                        <button
                          class="button button--form"
                          :disabled="getField('cdf_6').enabledReadOnly"
                          v-for="(option, index) in booleanOptions"
                          @click="getField('cdf_6').enabled = option.code;
                            validateRequiredButton('cdf_6')"
                          :class="{ 'is-active': getField('cdf_6').enabled === option.code }"
                          :key="index"
                        >
                          {{ option.description }}
                        </button>
                      </div>
                      <div v-else class="disable-cursor">
                        {{ getField('cdf_6').enabled === true ? 'Yes' : 'No' }}
                      </div>
                    </td>
                    <td>
                      <div v-if="editCurrentPanel">
                        <button
                          class="button button--form"
                          :disabled="getField('cdf_6').requiredReadOnly"
                          v-for="(option, index) in booleanOptions"
                          @click="getField('cdf_6').required = option.code;
                            validateRequiredButton('cdf_6', 'NOTENABLED')"
                          :class="{ 'is-active': getField('cdf_6').required === option.code }"
                          :key="index"
                        >
                          {{ option.description }}
                        </button>
                      </div>
                      <div v-else class="disable-cursor">
                        {{ getField('cdf_6').required === true ? 'Yes' : 'No' }}
                      </div>
                    </td>
                  </template>
                </tr>
                <tr :class="{ 'bottom-border': !editCurrentPanel }">
                  <td class="fields has-text-grey">
                    {{ getField('cdf_7').name }}
                  </td>
                  <es-loader v-if="!getField('cdf_7')" class="iconCenter"/>
                  <template v-else>
                    <td>
                      <div v-if="editCurrentPanel">
                        <input
                          class="input"
                          type="text"
                          v-model="getField('cdf_7').customLabel"
                          :title="getField('cdf_7').customLabel"
                          aria-label="Display Label"
                        />
                      </div>
                      <div v-else class="disable-cursor">
                        <input
                          class="input"
                          :class="{ 'is-static': !editCurrentPanel }"
                          :readonly="!editCurrentPanel"
                          :value="getField('cdf_7').customLabel"
                          :title="getField('cdf_7').customLabel"
                          aria-label="Display Label"
                        />
                      </div>
                    </td>
                    <td>
                      <div v-if="editCurrentPanel">
                        <button
                          class="button button--form"
                          :disabled="getField('cdf_7').enabledReadOnly"
                          v-for="(option, index) in booleanOptions"
                          @click="getField('cdf_7').enabled = option.code;
                            validateRequiredButton('cdf_7')"
                          :class="{ 'is-active': getField('cdf_7').enabled === option.code }"
                          :key="index"
                        >
                          {{ option.description }}
                        </button>
                      </div>
                      <div v-else class="disable-cursor">
                        {{ getField('cdf_7').enabled === true ? 'Yes' : 'No' }}
                      </div>
                    </td>
                    <td>
                      <div v-if="editCurrentPanel">
                        <button
                          class="button button--form"
                          :disabled="getField('cdf_7').requiredReadOnly"
                          v-for="(option, index) in booleanOptions"
                          @click="getField('cdf_7').required = option.code;
                            validateRequiredButton('cdf_7', 'NOTENABLED')"
                          :class="{ 'is-active': getField('cdf_7').required === option.code }"
                          :key="index"
                        >
                          {{ option.description }}
                        </button>
                      </div>
                      <div v-else class="disable-cursor">
                        {{ getField('cdf_7').required === true ? 'Yes' : 'No' }}
                      </div>
                    </td>
                  </template>
                </tr>
                <tr :class="{ 'bottom-border': !editCurrentPanel }">
                  <td class="fields has-text-grey">
                    {{ getField('cdf_8').name }}
                  </td>
                  <es-loader v-if="!getField('cdf_8')" class="iconCenter"/>
                  <template v-else>
                    <td>
                      <div v-if="editCurrentPanel">
                        <input
                          class="input"
                          type="text"
                          v-model="getField('cdf_8').customLabel"
                          :title="getField('cdf_8').customLabel"
                          aria-label="Display Label"
                        />
                      </div>
                      <div v-else class="disable-cursor">
                        <input
                          class="input"
                          :class="{ 'is-static': !editCurrentPanel }"
                          :readonly="!editCurrentPanel"
                          :value="getField('cdf_8').customLabel"
                          :title="getField('cdf_8').customLabel"
                          aria-label="Display Label"
                        />
                      </div>
                    </td>
                    <td>
                      <div v-if="editCurrentPanel">
                        <button
                          class="button button--form"
                          :disabled="getField('cdf_8').enabledReadOnly"
                          v-for="(option, index) in booleanOptions"
                          @click="getField('cdf_8').enabled = option.code;
                            validateRequiredButton('cdf_8')"
                          :class="{ 'is-active': getField('cdf_8').enabled === option.code }"
                          :key="index"
                        >
                          {{ option.description }}
                        </button>
                      </div>
                      <div v-else class="disable-cursor">
                        {{ getField('cdf_8').enabled === true ? 'Yes' : 'No' }}
                      </div>
                    </td>
                    <td>
                      <div v-if="editCurrentPanel">
                        <button
                          class="button button--form"
                          :disabled="getField('cdf_8').requiredReadOnly"
                          v-for="(option, index) in booleanOptions"
                          @click="getField('cdf_8').required = option.code;
                            validateRequiredButton('cdf_8', 'NOTENABLED')"
                          :class="{ 'is-active': getField('cdf_8').required === option.code }"
                          :key="index"
                        >
                          {{ option.description }}
                        </button>
                      </div>
                      <div v-else class="disable-cursor">
                        {{ getField('cdf_8').required === true ? 'Yes' : 'No' }}
                      </div>
                    </td>
                  </template>
                </tr>
                <tr :class="{ 'bottom-border': !editCurrentPanel }">
                  <td class="fields has-text-grey">
                    {{ getField('cdf_9').name }}
                  </td>
                  <es-loader v-if="!getField('cdf_9')" class="iconCenter"/>
                  <template v-else>
                    <td>
                      <div v-if="editCurrentPanel">
                        <input
                          class="input"
                          type="text"
                          v-model="getField('cdf_9').customLabel"
                          :title="getField('cdf_9').customLabel"
                          aria-label="Display Label"
                        />
                      </div>
                      <div v-else class="disable-cursor">
                        <input
                          class="input"
                          :class="{ 'is-static': !editCurrentPanel }"
                          :readonly="!editCurrentPanel"
                          :value="getField('cdf_9').customLabel"
                          :title="getField('cdf_9').customLabel"
                          aria-label="Display Label"
                        />
                      </div>
                    </td>
                    <td>
                      <div v-if="editCurrentPanel">
                        <button
                          class="button button--form"
                          :disabled="getField('cdf_9').enabledReadOnly"
                          v-for="(option, index) in booleanOptions"
                          @click="getField('cdf_9').enabled = option.code;
                            validateRequiredButton('cdf_9')"
                          :class="{ 'is-active': getField('cdf_9').enabled === option.code }"
                          :key="index"
                        >
                          {{ option.description }}
                        </button>
                      </div>
                      <div v-else class="disable-cursor">
                        {{ getField('cdf_9').enabled === true ? 'Yes' : 'No' }}
                      </div>
                    </td>
                    <td>
                      <div v-if="editCurrentPanel">
                        <button
                          class="button button--form"
                          :disabled="getField('cdf_9').requiredReadOnly"
                          v-for="(option, index) in booleanOptions"
                          @click="getField('cdf_9').required = option.code;
                            validateRequiredButton('cdf_9', 'NOTENABLED')"
                          :class="{ 'is-active': getField('cdf_9').required === option.code }"
                          :key="index"
                        >
                          {{ option.description }}
                        </button>
                      </div>
                      <div v-else class="disable-cursor">
                        {{ getField('cdf_9').required === true ? 'Yes' : 'No' }}
                      </div>
                    </td>
                  </template>
                </tr>
                <tr :class="{ 'bottom-border': !editCurrentPanel }">
                  <td class="fields has-text-grey">
                    {{ getField('cdf_10').name }}
                  </td>
                  <es-loader v-if="!getField('cdf_10')" class="iconCenter"/>
                  <template v-else>
                    <td>
                      <div v-if="editCurrentPanel">
                        <input
                          class="input"
                          type="text"
                          v-model="getField('cdf_10').customLabel"
                          :title="getField('cdf_10').customLabel"
                          aria-label="Display Label"
                        />
                      </div>
                      <div v-else class="disable-cursor">
                        <input
                          class="input"
                          :class="{ 'is-static': !editCurrentPanel }"
                          :readonly="!editCurrentPanel"
                          :value="getField('cdf_10').customLabel"
                          :title="getField('cdf_10').customLabel"
                          aria-label="Display Label"
                        />
                      </div>
                    </td>
                    <td>
                      <div v-if="editCurrentPanel">
                        <button
                          class="button button--form"
                          :disabled="getField('cdf_10').enabledReadOnly"
                          v-for="(option, index) in booleanOptions"
                          @click="getField('cdf_10').enabled = option.code;
                            validateRequiredButton('cdf_10')"
                          :class="{ 'is-active': getField('cdf_10').enabled === option.code }"
                          :key="index"
                        >
                          {{ option.description }}
                        </button>
                      </div>
                      <div v-else class="disable-cursor">
                        {{ getField('cdf_10').enabled === true ? 'Yes' : 'No' }}
                      </div>
                    </td>
                    <td>
                      <div v-if="editCurrentPanel">
                        <button
                          class="button button--form"
                          :disabled="getField('cdf_10').requiredReadOnly"
                          v-for="(option, index) in booleanOptions"
                          @click="getField('cdf_10').required = option.code;
                            validateRequiredButton('cdf_10', 'NOTENABLED')"
                          :class="{ 'is-active': getField('cdf_10').required === option.code }"
                          :key="index"
                        >
                          {{ option.description }}
                        </button>
                      </div>
                      <div v-else class="disable-cursor">
                        {{ getField('cdf_10').required === true ? 'Yes' : 'No' }}
                      </div>
                    </td>
                  </template>
                </tr>
                <tr>
                  <td class="fields has-text-grey">
                    {{ getField('cdf_11').name }}
                  </td>
                  <es-loader v-if="!getField('cdf_11')" class="iconCenter"/>
                  <template v-else>
                    <td>
                      <div v-if="editCurrentPanel">
                        <input
                          class="input"
                          type="text"
                          v-model="getField('cdf_11').customLabel"
                          :title="getField('cdf_11').customLabel"
                          aria-label="Display Label"
                        />
                      </div>
                      <div v-else class="disable-cursor">
                        <input
                          class="input"
                          :class="{ 'is-static': !editCurrentPanel }"
                          :readonly="!editCurrentPanel"
                          :value="getField('cdf_11').customLabel"
                          :title="getField('cdf_11').customLabel"
                          aria-label="Display Label"
                        />
                      </div>
                    </td>
                    <td>
                      <div v-if="editCurrentPanel">
                        <button
                          class="button button--form"
                          :disabled="getField('cdf_11').enabledReadOnly"
                          v-for="(option, index) in booleanOptions"
                          @click="getField('cdf_11').enabled = option.code;
                            validateRequiredButton('cdf_11')"
                          :class="{ 'is-active': getField('cdf_11').enabled === option.code }"
                          :key="index"
                        >
                          {{ option.description }}
                        </button>
                      </div>
                      <div v-else class="disable-cursor">
                        {{ getField('cdf_11').enabled === true ? 'Yes' : 'No' }}
                      </div>
                    </td>
                    <td>
                      <div v-if="editCurrentPanel">
                        <button
                          class="button button--form"
                          v-for="(option, index) in booleanOptions"
                          :disabled="getField('cdf_11').requiredReadOnly"
                          @click="getField('cdf_11').required = option.code;
                            validateRequiredButton('cdf_11', 'NOTENABLED')"
                          :class="{ 'is-active': getField('cdf_11').required === option.code }"
                          :key="index"
                        >
                          {{ option.description }}
                        </button>
                      </div>
                      <div v-else class="disable-cursor">
                        {{ getField('cdf_11').required === true ? 'Yes' : 'No' }}
                      </div>
                    </td>
                  </template>
                </tr>
              </tbody>
            </table>
          </template>
        </es-form>
        <es-form class="partition-border">
          <template #es-form-header>
            <div class="header-container">
              <div class="title is-6">
                Location Information
              </div>
              <div class="icon-right">
                <a role="button" tabindex="0"
                  @keyup.enter="toggleField('locationInformation')()">
                  <font-awesome-icon :icon="['far', 'caret-up']" size="lg"
                    @click="toggleField('locationInformation')()"
                    v-if="locationInformation"
                    title="Hide Details"
                  />
                  <font-awesome-icon :icon="['far', 'caret-down']" size="lg"
                    @click="toggleField('locationInformation')()"
                    v-if="!locationInformation"
                    title="View Details"
                  />
                </a>
              </div>
            </div>
          </template>
          <template #es-form>
            <table v-if="locationInformation">
              <tbody>
                <tr>
                  <th class="rule left-padding">
                    eScholar Field
                  </th>
                  <th class="rule">
                    Display Label
                  </th>
                  <th class="rule">
                    Enabled
                  </th>
                  <th class="rule">
                    Required
                  </th>
                </tr>
                <tr :class="{ 'bottom-border': !editCurrentPanel }">
                  <td class="fields has-text-grey">
                    {{ getField('SCHOOL_YEAR').name }}
                  </td>
                  <es-loader v-if="!getField('SCHOOL_YEAR')" class="iconCenter"/>
                  <template v-else>
                    <td>
                      <div v-if="editCurrentPanel">
                        <input
                          class="input"
                          type="text"
                          v-model="getField('SCHOOL_YEAR').customLabel"
                          :title="getField('SCHOOL_YEAR').customLabel"
                          aria-label="Display Label"
                        />
                      </div>
                      <div v-else class="disable-cursor">
                        <input
                          class="input"
                          :class="{ 'is-static': !editCurrentPanel }"
                          :readonly="!editCurrentPanel"
                          :value="getField('SCHOOL_YEAR').customLabel"
                          :title="getField('SCHOOL_YEAR').customLabel"
                          aria-label="Display Label"
                        />
                      </div>
                    </td>
                    <td>
                      <div v-if="editCurrentPanel">
                        <button
                          class="button button--form"
                          :disabled="getField('SCHOOL_YEAR').enabledReadOnly"
                          v-for="(option, index) in booleanOptions"
                          @click="getField('SCHOOL_YEAR').enabled = option.code;
                            validateRequiredButton('SCHOOL_YEAR')"
                          :class="{ 'is-active': getField('SCHOOL_YEAR').enabled === option.code }"
                          :key="index"
                        >
                          {{ option.description }}
                        </button>
                      </div>
                      <div v-else class="disable-cursor">
                        {{ getField('SCHOOL_YEAR').enabled === true ? 'Yes' : 'No' }}
                      </div>
                    </td>
                    <td>
                      <div v-if="editCurrentPanel">
                        <button
                          class="button button--form"
                          :disabled="getField('SCHOOL_YEAR').requiredReadOnly"
                          v-for="(option, index) in booleanOptions"
                          @click="getField('SCHOOL_YEAR').required = option.code;
                            validateRequiredButton('SCHOOL_YEAR', 'NOTENABLED')"
                          :class="{ 'is-active': getField('SCHOOL_YEAR').required === option.code }"
                          :key="index"
                        >
                          {{ option.description }}
                        </button>
                      </div>
                      <div v-else class="disable-cursor">
                        {{ getField('SCHOOL_YEAR').required === true ? 'Yes' : 'No' }}
                      </div>
                    </td>
                  </template>
                </tr>
                <tr :class="{ 'bottom-border': !editCurrentPanel }">
                  <td class="fields has-text-grey">
                    {{ getField('DISTRICT_CODE').name }}
                  </td>
                  <es-loader v-if="!getField('DISTRICT_CODE')" class="iconCenter"/>
                  <template v-else>
                    <td>
                      <div v-if="editCurrentPanel">
                        <input
                          class="input"
                          type="text"
                          v-model="getField('DISTRICT_CODE').customLabel"
                          :title="getField('DISTRICT_CODE').customLabel"
                          aria-label="Display Label"
                        />
                      </div>
                      <div v-else class="disable-cursor">
                        <input
                          class="input"
                          :class="{ 'is-static': !editCurrentPanel }"
                          :readonly="!editCurrentPanel"
                          :value="getField('DISTRICT_CODE').customLabel"
                          :title="getField('DISTRICT_CODE').customLabel"
                          aria-label="Display Label"
                        />
                      </div>
                    </td>
                    <td>
                      <div v-if="editCurrentPanel">
                        <button
                          class="button button--form"
                          :disabled="getField('DISTRICT_CODE').enabledReadOnly"
                          v-for="(option, index) in booleanOptions"
                          @click="getField('DISTRICT_CODE').enabled = option.code;
                            validateRequiredButton('DISTRICT_CODE')"
                          :class="{'is-active': getField('DISTRICT_CODE').enabled === option.code}"
                          :key="index"
                        >
                          {{ option.description }}
                        </button>
                      </div>
                      <div v-else class="disable-cursor">
                        {{ getField('DISTRICT_CODE').enabled === true ? 'Yes' : 'No' }}
                      </div>
                    </td>
                    <td>
                      <div v-if="editCurrentPanel">
                        <button
                          class="button button--form"
                          :disabled="getField('DISTRICT_CODE').requiredReadOnly"
                          v-for="(option, index) in booleanOptions"
                          @click="getField('DISTRICT_CODE').required = option.code;
                            validateRequiredButton('DISTRICT_CODE', 'NOTENABLED')"
                          :class="{'is-active': getField('DISTRICT_CODE').required === option.code}"
                          :key="index"
                        >
                          {{ option.description }}
                        </button>
                      </div>
                      <div v-else class="disable-cursor">
                        {{ getField('DISTRICT_CODE').required === true ? 'Yes' : 'No' }}
                      </div>
                    </td>
                  </template>
                </tr>
                <tr :class="{ 'bottom-border': !editCurrentPanel }">
                  <td class="fields has-text-grey">
                    {{ getField('SCHOOL_CODE').name }}
                  </td>
                  <es-loader v-if="!getField('SCHOOL_CODE')" class="iconCenter"/>
                  <template v-else>
                    <td>
                      <div v-if="editCurrentPanel">
                        <input
                          class="input"
                          type="text"
                          v-model="getField('SCHOOL_CODE').customLabel"
                          :title="getField('SCHOOL_CODE').customLabel"
                          aria-label="Display Label"
                        />
                      </div>
                      <div v-else class="disable-cursor">
                        <input
                          class="input"
                          :class="{ 'is-static': !editCurrentPanel }"
                          :readonly="!editCurrentPanel"
                          :value="getField('SCHOOL_CODE').customLabel"
                          :title="getField('SCHOOL_CODE').customLabel"
                          aria-label="Display Label"
                        />
                      </div>
                    </td>
                    <td>
                      <div v-if="editCurrentPanel">
                        <button
                          class="button button--form"
                          :disabled="getField('SCHOOL_CODE').enabledReadOnly"
                          v-for="(option, index) in booleanOptions"
                          @click="getField('SCHOOL_CODE').enabled = option.code;
                            validateRequiredButton('SCHOOL_CODE')"
                          :class="{ 'is-active': getField('SCHOOL_CODE').enabled === option.code }"
                          :key="index"
                        >
                          {{ option.description }}
                        </button>
                      </div>
                      <div v-else class="disable-cursor">
                        {{ getField('SCHOOL_CODE').enabled === true ? 'Yes' : 'No' }}
                      </div>
                    </td>
                    <td>
                      <div v-if="editCurrentPanel">
                        <button
                          class="button button--form"
                          :disabled="getField('SCHOOL_CODE').requiredReadOnly"
                          v-for="(option, index) in booleanOptions"
                          @click="getField('SCHOOL_CODE').required = option.code;
                            validateRequiredButton('SCHOOL_CODE', 'NOTENABLED')"
                          :class="{ 'is-active': getField('SCHOOL_CODE').required === option.code }"
                          :key="index"
                        >
                          {{ option.description }}
                        </button>
                      </div>
                      <div v-else class="disable-cursor">
                        {{ getField('SCHOOL_CODE').required === true ? 'Yes' : 'No' }}
                      </div>
                    </td>
                  </template>
                </tr>
                <tr :class="{ 'bottom-border': !editCurrentPanel }">
                  <td class="fields has-text-grey">
                    {{ getField('RES_DISTRICT_CODE').name }}
                  </td>
                  <es-loader v-if="!getField('RES_DISTRICT_CODE')" class="iconCenter"/>
                  <template v-else>
                    <td>
                      <div v-if="editCurrentPanel">
                        <input
                          class="input"
                          type="text"
                          v-model="getField('RES_DISTRICT_CODE').customLabel"
                          :title="getField('RES_DISTRICT_CODE').customLabel"
                          aria-label="Display Label"
                        />
                      </div>
                      <div v-else class="disable-cursor">
                        <input
                          class="input"
                          :class="{ 'is-static': !editCurrentPanel }"
                          :readonly="!editCurrentPanel"
                          :value="getField('RES_DISTRICT_CODE').customLabel"
                          :title="getField('RES_DISTRICT_CODE').customLabel"
                          aria-label="Display Label"
                        />
                      </div>
                    </td>
                    <td>
                      <div v-if="editCurrentPanel">
                        <button
                          class="button button--form"
                          :disabled="getField('RES_DISTRICT_CODE').enabledReadOnly"
                          v-for="(option, index) in booleanOptions"
                          @click="getField('RES_DISTRICT_CODE').enabled = option.code;
                            validateRequiredButton('RES_DISTRICT_CODE')"
                          :class="{ 'is-active': getField('RES_DISTRICT_CODE')
                            .enabled === option.code }"
                          :key="index"
                        >
                          {{ option.description }}
                        </button>
                      </div>
                      <div v-else class="disable-cursor">
                        {{ getField('RES_DISTRICT_CODE').enabled === true ? 'Yes' : 'No' }}
                      </div>
                    </td>
                    <td>
                      <div v-if="editCurrentPanel">
                        <button
                          class="button button--form"
                          :disabled="getField('RES_DISTRICT_CODE').requiredReadOnly"
                          v-for="(option, index) in booleanOptions"
                          @click="getField('RES_DISTRICT_CODE').required = option.code;
                            validateRequiredButton('RES_DISTRICT_CODE', 'NOTENABLED')"
                          :class="{ 'is-active': getField('RES_DISTRICT_CODE')
                            .required === option.code }"
                          :key="index"
                        >
                          {{ option.description }}
                        </button>
                      </div>
                      <div v-else class="disable-cursor">
                        {{ getField('RES_DISTRICT_CODE').required === true ? 'Yes' : 'No' }}
                      </div>
                    </td>
                  </template>
                </tr>
                <tr :class="{ 'bottom-border': !editCurrentPanel }">
                  <td class="fields has-text-grey">
                    {{ getField('LOCAL_STUDENT_ID').name }}
                  </td>
                  <es-loader v-if="!getField('LOCAL_STUDENT_ID')" class="iconCenter"/>
                  <template v-else>
                    <td>
                      <div v-if="editCurrentPanel">
                        <input
                          class="input"
                          type="text"
                          v-model="getField('LOCAL_STUDENT_ID').customLabel"
                          :title="getField('LOCAL_STUDENT_ID').customLabel"
                          aria-label="Display Label"
                        />
                      </div>
                      <div v-else class="disable-cursor">
                        <input
                          class="input"
                          :class="{ 'is-static': !editCurrentPanel }"
                          :readonly="!editCurrentPanel"
                          :value="getField('LOCAL_STUDENT_ID').customLabel"
                          :title="getField('LOCAL_STUDENT_ID').customLabel"
                          aria-label="Display Label"
                        >
                      </div>
                    </td>
                    <td>
                      <div v-if="editCurrentPanel">
                        <button
                          class="button button--form"
                          :disabled="getField('LOCAL_STUDENT_ID').enabledReadOnly"
                          v-for="(option, index) in booleanOptions"
                          @click="getField('LOCAL_STUDENT_ID').enabled = option.code;
                            validateRequiredButton('LOCAL_STUDENT_ID')"
                          :class="{ 'is-active': getField('LOCAL_STUDENT_ID')
                            .enabled === option.code }"
                          :key="index"
                        >
                          {{ option.description }}
                        </button>
                      </div>
                      <div v-else class="disable-cursor">
                        {{ getField('LOCAL_STUDENT_ID').enabled === true ? 'Yes' : 'No' }}
                      </div>
                    </td>
                    <td>
                      <div v-if="editCurrentPanel">
                        <button
                          class="button button--form"
                          :disabled="getField('LOCAL_STUDENT_ID').requiredReadOnly"
                          v-for="(option, index) in booleanOptions"
                          @click="getField('LOCAL_STUDENT_ID').required = option.code;
                            validateRequiredButton('LOCAL_STUDENT_ID', 'NOTENABLED')"
                          :class="{ 'is-active': getField('LOCAL_STUDENT_ID')
                            .required === option.code }"
                          :key="index"
                        >
                          {{ option.description }}
                        </button>
                      </div>
                      <div v-else class="disable-cursor">
                        {{ getField('LOCAL_STUDENT_ID').required === true ? 'Yes' : 'No' }}
                      </div>
                    </td>
                  </template>
                </tr>
                <tr :class="{ 'bottom-border': !editCurrentPanel }">
                  <td class="fields has-text-grey">
                    {{ getField('SOURCE_CODE').name }}
                  </td>
                  <es-loader v-if="!getField('SOURCE_CODE')" class="iconCenter"/>
                  <template v-else>
                    <td>
                      <div v-if="editCurrentPanel">
                        <input
                          class="input"
                          type="text"
                          v-model="getField('SOURCE_CODE').customLabel"
                          :title="getField('SOURCE_CODE').customLabel"
                          aria-label="Display Label"
                        />
                      </div>
                      <div v-else class="disable-cursor">
                        <input
                          class="input"
                          :class="{ 'is-static': !editCurrentPanel }"
                          :readonly="!editCurrentPanel"
                          :value="getField('SOURCE_CODE').customLabel"
                          :title="getField('SOURCE_CODE').customLabel"
                          aria-label="Display Label"
                        />
                      </div>
                    </td>
                    <td>
                      <div v-if="editCurrentPanel">
                        <button
                          class="button button--form"
                          :disabled="getField('SOURCE_CODE').enabledReadOnly"
                          v-for="(option, index) in booleanOptions"
                          @click="getField('SOURCE_CODE').enabled = option.code;
                            validateRequiredButton('SOURCE_CODE')"
                          :class="{ 'is-active': getField('SOURCE_CODE').enabled === option.code }"
                          :key="index"
                        >
                          {{ option.description }}
                        </button>
                      </div>
                      <div v-else class="disable-cursor">
                        {{ getField('SOURCE_CODE').enabled === true ? 'Yes' : 'No' }}
                      </div>
                    </td>
                    <td>
                      <div v-if="editCurrentPanel">
                        <button
                          class="button button--form"
                          :disabled="getField('SOURCE_CODE').requiredReadOnly"
                          v-for="(option, index) in booleanOptions"
                          @click="getField('SOURCE_CODE').required = option.code;
                            validateRequiredButton('SOURCE_CODE', 'NOTENABLED')"
                          :class="{ 'is-active': getField('SOURCE_CODE').required === option.code }"
                          :key="index"
                        >
                          {{ option.description }}
                        </button>
                      </div>
                      <div v-else class="disable-cursor">
                        {{ getField('SOURCE_CODE').required === true ? 'Yes' : 'No' }}
                      </div>
                    </td>
                  </template>
                </tr>
                <tr :class="{ 'bottom-border': !editCurrentPanel }">
                  <td class="fields has-text-grey">
                    {{ getField('ALT_STUDENT_ID').name }}
                  </td>
                  <es-loader v-if="!getField('ALT_STUDENT_ID')" class="iconCenter"/>
                  <template v-else>
                    <td>
                      <div v-if="editCurrentPanel">
                        <input
                          class="input"
                          type="text"
                          v-model="getField('ALT_STUDENT_ID').customLabel"
                          :title="getField('ALT_STUDENT_ID').customLabel"
                          aria-label="Display Label"
                        />
                      </div>
                      <div v-else class="disable-cursor">
                        <input
                          class="input"
                          :class="{ 'is-static': !editCurrentPanel }"
                          :readonly="!editCurrentPanel"
                          :value="getField('ALT_STUDENT_ID').customLabel"
                          :title="getField('ALT_STUDENT_ID').customLabel"
                          aria-label="Display Label"
                        />
                      </div>
                    </td>
                    <td>
                      <div v-if="editCurrentPanel">
                        <button
                          class="button button--form"
                          :disabled="getField('ALT_STUDENT_ID').enabledReadOnly"
                          v-for="(option, index) in booleanOptions"
                          @click="getField('ALT_STUDENT_ID').enabled = option.code;
                            validateRequiredButton('ALT_STUDENT_ID')"
                          :class="{ 'is-active': getField('ALT_STUDENT_ID')
                            .enabled === option.code }"
                          :key="index"
                        >
                          {{ option.description }}
                        </button>
                      </div>
                      <div v-else class="disable-cursor">
                        {{ getField('ALT_STUDENT_ID').enabled === true ? 'Yes' : 'No' }}
                      </div>
                    </td>
                    <td>
                      <div v-if="editCurrentPanel">
                        <button
                          class="button button--form"
                          :disabled="getField('ALT_STUDENT_ID').requiredReadOnly"
                          v-for="(option, index) in booleanOptions"
                          @click="getField('ALT_STUDENT_ID').required = option.code;
                            validateRequiredButton('ALT_STUDENT_ID', 'NOTENABLED')"
                          :class="{ 'is-active': getField('ALT_STUDENT_ID')
                            .required === option.code }"
                          :key="index"
                        >
                          {{ option.description }}
                        </button>
                      </div>
                      <div v-else class="disable-cursor">
                        {{ getField('ALT_STUDENT_ID').required === true ? 'Yes' : 'No' }}
                      </div>
                    </td>
                  </template>
                </tr>
                <tr :class="{ 'bottom-border': !editCurrentPanel }">
                  <td class="fields has-text-grey">
                    {{ getField('ALT_SOURCE_SIS_CODE').name }}
                  </td>
                  <es-loader v-if="!getField('ALT_SOURCE_SIS_CODE')" class="iconCenter"/>
                  <template v-else>
                    <td>
                      <div v-if="editCurrentPanel">
                        <input
                          class="input"
                          type="text"
                          v-model="getField('ALT_SOURCE_SIS_CODE').customLabel"
                          :title="getField('ALT_SOURCE_SIS_CODE').customLabel"
                          aria-label="Display Label"
                        />
                      </div>
                      <div v-else class="disable-cursor">
                        <input
                          class="input"
                          :class="{ 'is-static': !editCurrentPanel }"
                          :readonly="!editCurrentPanel"
                          :value="getField('ALT_SOURCE_SIS_CODE').customLabel"
                          :title="getField('ALT_SOURCE_SIS_CODE').customLabel"
                          aria-label="Display Label"
                        />
                      </div>
                    </td>
                    <td>
                      <div v-if="editCurrentPanel">
                        <button
                          class="button button--form"
                          :disabled="getField('ALT_SOURCE_SIS_CODE').enabledReadOnly"
                          v-for="(option, index) in booleanOptions"
                          @click="getField('ALT_SOURCE_SIS_CODE').enabled = option.code;
                            validateRequiredButton('ALT_SOURCE_SIS_CODE')"
                          :class="{ 'is-active': getField('ALT_SOURCE_SIS_CODE')
                            .enabled === option.code }"
                          :key="index"
                        >
                          {{ option.description }}
                        </button>
                      </div>
                      <div v-else class="disable-cursor">
                        {{ getField('ALT_SOURCE_SIS_CODE').enabled === true ? 'Yes' : 'No' }}
                      </div>
                    </td>
                    <td>
                      <div v-if="editCurrentPanel">
                        <button
                          class="button button--form"
                          :disabled="getField('ALT_SOURCE_SIS_CODE').requiredReadOnly"
                          v-for="(option, index) in booleanOptions"
                          @click="getField('ALT_SOURCE_SIS_CODE').required = option.code;
                            validateRequiredButton('ALT_SOURCE_SIS_CODE', 'NOTENABLED')"
                          :class="{ 'is-active': getField('ALT_SOURCE_SIS_CODE')
                            .required === option.code }"
                          :key="index"
                        >
                          {{ option.description }}
                        </button>
                      </div>
                      <div v-else class="disable-cursor">
                        {{ getField('ALT_SOURCE_SIS_CODE').required === true ? 'Yes' : 'No' }}
                      </div>
                    </td>
                  </template>
                </tr>
                <tr :class="{ 'bottom-border': !editCurrentPanel }">
                  <td class="fields has-text-grey">
                    {{ getField('ACTIVE').name }}
                  </td>
                  <es-loader v-if="!getField('ACTIVE')" class="iconCenter"/>
                  <template v-else>
                    <td>
                      <div v-if="editCurrentPanel">
                        <input
                          class="input"
                          type="text"
                          v-model="getField('ACTIVE').customLabel"
                          :title="getField('ACTIVE').customLabel"
                          aria-label="Display Label"
                        />
                      </div>
                      <div v-else class="disable-cursor">
                        <input
                          class="input"
                          :class="{ 'is-static': !editCurrentPanel }"
                          :readonly="!editCurrentPanel"
                          :value="getField('ACTIVE').customLabel"
                          :title="getField('ACTIVE').customLabel"
                          aria-label="Display Label"
                        />
                      </div>
                    </td>
                    <td>
                      <div v-if="editCurrentPanel">
                        <button
                          class="button button--form"
                          :disabled="getField('ACTIVE').enabledReadOnly"
                          v-for="(option, index) in booleanOptions"
                          @click="getField('ACTIVE').enabled = option.code;
                            validateRequiredButton('ACTIVE')"
                          :class="{ 'is-active': getField('ACTIVE').enabled === option.code }"
                          :key="index"
                        >
                          {{ option.description }}
                        </button>
                      </div>
                      <div v-else class="disable-cursor">
                        {{ getField('ACTIVE').enabled === true ? 'Yes' : 'No' }}
                      </div>
                    </td>
                    <td>
                      <div v-if="editCurrentPanel">
                        <button
                          class="button button--form"
                          :disabled="getField('ACTIVE').requiredReadOnly"
                          v-for="(option, index) in booleanOptions"
                          @click="getField('ACTIVE').required = option.code;
                            validateRequiredButton('ACTIVE', 'NOTENABLED')"
                          :class="{ 'is-active': getField('ACTIVE').required === option.code }"
                          :key="index"
                        >
                          {{ option.description }}
                        </button>
                      </div>
                      <div v-else class="disable-cursor">
                        {{ getField('ACTIVE').required === true ? 'Yes' : 'No' }}
                      </div>
                    </td>
                  </template>
                </tr>
                <tr :class="{ 'bottom-border': !editCurrentPanel }">
                  <td class="fields has-text-grey">
                    {{ getField('SOURCE_REFRESH_DATE').name }}
                  </td>
                  <es-loader v-if="!getField('SOURCE_REFRESH_DATE')" class="iconCenter"/>
                  <template v-else>
                    <td>
                      <div v-if="editCurrentPanel">
                        <input
                          class="input"
                          type="text"
                          v-model="getField('SOURCE_REFRESH_DATE').customLabel"
                          :title="getField('SOURCE_REFRESH_DATE').customLabel"
                          aria-label="Display Label"
                        />
                      </div>
                      <div v-else class="disable-cursor">
                        <input
                          class="input"
                          :class="{ 'is-static': !editCurrentPanel }"
                          :readonly="!editCurrentPanel"
                          :value="getField('SOURCE_REFRESH_DATE').customLabel"
                          :title="getField('SOURCE_REFRESH_DATE').customLabel"
                          aria-label="Display Label"
                        />
                      </div>
                    </td>
                    <td>
                      <div v-if="editCurrentPanel">
                        <button
                          class="button button--form"
                          :disabled="getField('SOURCE_REFRESH_DATE').enabledReadOnly"
                          v-for="(option, index) in booleanOptions"
                          @click="getField('SOURCE_REFRESH_DATE').enabled = option.code;
                            validateRequiredButton('SOURCE_REFRESH_DATE')"
                          :class="{ 'is-active': getField('SOURCE_REFRESH_DATE')
                            .enabled === option.code }"
                          :key="index"
                        >
                          {{ option.description }}
                        </button>
                      </div>
                      <div v-else class="disable-cursor">
                        {{ getField('SOURCE_REFRESH_DATE').enabled === true ? 'Yes' : 'No' }}
                      </div>
                    </td>
                    <td>
                      <div v-if="editCurrentPanel">
                        <button
                          class="button button--form"
                          :disabled="getField('SOURCE_REFRESH_DATE').requiredReadOnly"
                          v-for="(option, index) in booleanOptions"
                          @click="getField('SOURCE_REFRESH_DATE').required = option.code;
                            validateRequiredButton('SOURCE_REFRESH_DATE', 'NOTENABLED')"
                          :class="{ 'is-active': getField('SOURCE_REFRESH_DATE')
                            .required === option.code }"
                          :key="index"
                        >
                          {{ option.description }}
                        </button>
                      </div>
                      <div v-else class="disable-cursor">
                        {{ getField('SOURCE_REFRESH_DATE').required === true ? 'Yes' : 'No' }}
                      </div>
                    </td>
                  </template>
                </tr>
                <tr :class="{ 'bottom-border': !editCurrentPanel }">
                  <td class="fields has-text-grey">
                    {{ getField('LOCATION_COMMENT').name }}
                  </td>
                  <es-loader v-if="!getField('LOCATION_COMMENT')" class="iconCenter"/>
                  <template v-else>
                    <td>
                      <div v-if="editCurrentPanel">
                        <input
                          class="input"
                          type="text"
                          v-model="getField('LOCATION_COMMENT').customLabel"
                          :title="getField('LOCATION_COMMENT').customLabel"
                          aria-label="Display Label"
                        />
                      </div>
                      <div v-else class="disable-cursor">
                        <input
                          class="input"
                          :class="{ 'is-static': !editCurrentPanel }"
                          :readonly="!editCurrentPanel"
                          :value="getField('LOCATION_COMMENT').customLabel"
                          :title="getField('LOCATION_COMMENT').customLabel"
                          aria-label="Display Label"
                        />
                      </div>
                    </td>
                    <td>
                      <div v-if="editCurrentPanel">
                        <button
                          class="button button--form"
                          :disabled="getField('LOCATION_COMMENT').enabledReadOnly"
                          v-for="(option, index) in booleanOptions"
                          @click="getField('LOCATION_COMMENT').enabled = option.code;
                            validateRequiredButton('LOCATION_COMMENT')"
                          :class="{ 'is-active': getField('LOCATION_COMMENT')
                            .enabled === option.code }"
                          :key="index"
                        >
                          {{ option.description }}
                        </button>
                      </div>
                      <div v-else class="disable-cursor">
                        {{ getField('LOCATION_COMMENT').enabled === true ? 'Yes' : 'No' }}
                      </div>
                    </td>
                    <td>
                      <div v-if="editCurrentPanel">
                        <button
                          class="button button--form"
                          :disabled="getField('LOCATION_COMMENT').requiredReadOnly"
                          v-for="(option, index) in booleanOptions"
                          @click="getField('LOCATION_COMMENT').required = option.code;
                            validateRequiredButton('LOCATION_COMMENT', 'NOTENABLED')"
                          :class="{ 'is-active': getField('LOCATION_COMMENT')
                            .required === option.code }"
                          :key="index"
                        >
                          {{ option.description }}
                        </button>
                      </div>
                      <div v-else class="disable-cursor">
                        {{ getField('LOCATION_COMMENT').required === true ? 'Yes' : 'No' }}
                      </div>
                    </td>
                  </template>
                </tr>
                <tr :class="{ 'bottom-border': !editCurrentPanel }">
                  <td class="fields has-text-grey">
                    {{ getField('GRADE_CODE').name }}
                  </td>
                  <es-loader v-if="!getField('GRADE_CODE')" class="iconCenter"/>
                  <template v-else>
                    <td>
                      <div v-if="editCurrentPanel">
                        <input
                          class="input"
                          type="text"
                          v-model="getField('GRADE_CODE').customLabel"
                          aria-label="Display Label"
                        />
                      </div>
                      <div v-else class="disable-cursor">
                        <input
                          class="input"
                          :class="{ 'is-static': !editCurrentPanel }"
                          :readonly="!editCurrentPanel"
                          :value="getField('GRADE_CODE').customLabel"
                          :title="getField('GRADE_CODE').customLabel"
                          aria-label="Display Label"
                        />
                      </div>
                    </td>
                    <td>
                      <div v-if="editCurrentPanel">
                        <button
                          class="button button--form"
                          :disabled="getField('GRADE_CODE').enabledReadOnly"
                          v-for="(option, index) in booleanOptions"
                          @click="getField('GRADE_CODE').enabled = option.code;
                            validateRequiredButton('GRADE_CODE')"
                          :class="{ 'is-active': getField('GRADE_CODE').enabled === option.code }"
                          :key="index"
                        >
                          {{ option.description }}
                        </button>
                      </div>
                      <div v-else class="disable-cursor">
                        {{ getField('GRADE_CODE').enabled === true ? 'Yes' : 'No' }}
                      </div>
                    </td>
                    <td>
                      <div v-if="editCurrentPanel">
                        <button
                          class="button button--form"
                          :disabled="getField('GRADE_CODE').requiredReadOnly"
                          v-for="(option, index) in booleanOptions"
                          @click="getField('GRADE_CODE').required = option.code;
                            validateRequiredButton('GRADE_CODE', 'NOTENABLED')"
                          :class="{ 'is-active': getField('GRADE_CODE').required === option.code }"
                          :key="index"
                        >
                          {{ option.description }}
                        </button>
                      </div>
                      <div v-else class="disable-cursor">
                        {{ getField('GRADE_CODE').required === true ? 'Yes' : 'No' }}
                      </div>
                    </td>
                  </template>
                </tr>
                <tr :class="{ 'bottom-border': !editCurrentPanel }">
                  <td class="fields has-text-grey">
                    {{ getField('HIRE_DATE').name }}
                  </td>
                  <es-loader v-if="!getField('HIRE_DATE')" class="iconCenter"/>
                  <template v-else>
                    <td>
                      <div v-if="editCurrentPanel">
                        <input
                          class="input"
                          type="text"
                          v-model="getField('HIRE_DATE').customLabel"
                          :title="getField('HIRE_DATE').customLabel"
                          aria-label="Display Label"
                        />
                      </div>
                      <div v-else class="disable-cursor">
                        <input
                          class="input"
                          :class="{ 'is-static': !editCurrentPanel }"
                          :readonly="!editCurrentPanel"
                          :value="getField('HIRE_DATE').customLabel"
                          :title="getField('HIRE_DATE').customLabel"
                          aria-label="Display Label"
                        />
                      </div>
                    </td>
                    <td>
                      <div v-if="editCurrentPanel">
                        <button
                          class="button button--form"
                          :disabled="getField('HIRE_DATE').enabledReadOnly"
                          v-for="(option, index) in booleanOptions"
                          @click="getField('HIRE_DATE').enabled = option.code;
                            validateRequiredButton('HIRE_DATE')"
                          :class="{ 'is-active': getField('HIRE_DATE').enabled === option.code }"
                          :key="index"
                        >
                          {{ option.description }}
                        </button>
                      </div>
                      <div v-else class="disable-cursor">
                        {{ getField('HIRE_DATE').enabled === true ? 'Yes' : 'No' }}
                      </div>
                    </td>
                    <td>
                      <div v-if="editCurrentPanel">
                        <button
                          class="button button--form"
                          :disabled="getField('HIRE_DATE').requiredReadOnly"
                          v-for="(option, index) in booleanOptions"
                          @click="getField('HIRE_DATE').required = option.code;
                            validateRequiredButton('HIRE_DATE', 'NOTENABLED')"
                          :class="{ 'is-active': getField('HIRE_DATE').required === option.code }"
                          :key="index"
                        >
                          {{ option.description }}
                        </button>
                      </div>
                      <div v-else class="disable-cursor">
                        {{ getField('HIRE_DATE').required === true ? 'Yes' : 'No' }}
                      </div>
                    </td>
                  </template>
                </tr>
                <tr :class="{ 'bottom-border': !editCurrentPanel }">
                  <td class="fields has-text-grey">
                    {{ getField('SALARY').name }}
                  </td>
                  <es-loader v-if="!getField('SALARY')" class="iconCenter"/>
                  <template v-else>
                    <td>
                      <div v-if="editCurrentPanel">
                        <input
                          class="input"
                          type="text"
                          v-model="getField('SALARY').customLabel"
                          :title="getField('SALARY').customLabel"
                          aria-label="Display Label"
                        />
                      </div>
                      <div v-else class="disable-cursor">
                        <input
                          class="input"
                          :class="{ 'is-static': !editCurrentPanel }"
                          :readonly="!editCurrentPanel"
                          :value="getField('SALARY').customLabel"
                          :title="getField('SALARY').customLabel"
                          aria-label="Display Label"
                        />
                      </div>
                    </td>
                    <td>
                      <div v-if="editCurrentPanel">
                        <button
                          class="button button--form"
                          :disabled="getField('SALARY').enabledReadOnly"
                          v-for="(option, index) in booleanOptions"
                          @click="getField('SALARY').enabled = option.code;
                            validateRequiredButton('SALARY')"
                          :class="{ 'is-active': getField('SALARY').enabled === option.code }"
                          :key="index"
                        >
                          {{ option.description }}
                        </button>
                      </div>
                      <div v-else class="disable-cursor">
                        {{ getField('SALARY').enabled === true ? 'Yes' : 'No' }}
                      </div>
                    </td>
                    <td>
                      <div v-if="editCurrentPanel">
                        <button
                          class="button button--form"
                          :disabled="getField('SALARY').requiredReadOnly"
                          v-for="(option, index) in booleanOptions"
                          @click="getField('SALARY').required = option.code;
                            validateRequiredButton('SALARY', 'NOTENABLED')"
                          :class="{ 'is-active': getField('SALARY').required === option.code }"
                          :key="index"
                        >
                          {{ option.description }}
                        </button>
                      </div>
                      <div v-else class="disable-cursor">
                        {{ getField('SALARY').required === true ? 'Yes' : 'No' }}
                      </div>
                    </td>
                  </template>
                </tr>
                <tr :class="{ 'bottom-border': !editCurrentPanel }">
                  <td class="fields has-text-grey">
                    {{ getField('JOB_CLASS_CODE').name }}
                  </td>
                  <es-loader v-if="!getField('JOB_CLASS_CODE')" class="iconCenter"/>
                  <template v-else>
                    <td>
                      <div v-if="editCurrentPanel">
                        <input
                          class="input"
                          type="text"
                          v-model="getField('JOB_CLASS_CODE').customLabel"
                          :title="getField('JOB_CLASS_CODE').customLabel"
                          aria-label="Display Label"
                        />
                      </div>
                      <div v-else class="disable-cursor">
                        <input
                          class="input"
                          :class="{ 'is-static': !editCurrentPanel }"
                          :readonly="!editCurrentPanel"
                          :value="getField('JOB_CLASS_CODE').customLabel"
                          :title="getField('JOB_CLASS_CODE').customLabel"
                          aria-label="Display Label"
                        />
                      </div>
                    </td>
                    <td>
                      <div v-if="editCurrentPanel">
                        <button
                          class="button button--form"
                          :disabled="getField('JOB_CLASS_CODE').enabledReadOnly"
                          v-for="(option, index) in booleanOptions"
                          @click="getField('JOB_CLASS_CODE').enabled = option.code;
                            validateRequiredButton('JOB_CLASS_CODE')"
                          :class="{ 'is-active': getField('JOB_CLASS_CODE')
                            .enabled === option.code }"
                          :key="index"
                        >
                          {{ option.description }}
                        </button>
                      </div>
                      <div v-else class="disable-cursor">
                        {{ getField('JOB_CLASS_CODE').enabled === true ? 'Yes' : 'No' }}
                      </div>
                    </td>
                    <td>
                      <div v-if="editCurrentPanel">
                        <button
                          class="button button--form"
                          :disabled="getField('JOB_CLASS_CODE').requiredReadOnly"
                          v-for="(option, index) in booleanOptions"
                          @click="getField('JOB_CLASS_CODE').required = option.code;
                            validateRequiredButton('JOB_CLASS_CODE', 'NOTENABLED')"
                          :class="{ 'is-active': getField('JOB_CLASS_CODE')
                            .required === option.code }"
                          :key="index"
                        >
                          {{ option.description }}
                        </button>
                      </div>
                      <div v-else class="disable-cursor">
                        {{ getField('JOB_CLASS_CODE').required === true ? 'Yes' : 'No' }}
                      </div>
                    </td>
                  </template>
                </tr>
                <tr :class="{ 'bottom-border': !editCurrentPanel }">
                  <td class="fields has-text-grey">
                    {{ getField('JOB_ASSIGNMENT_1').name }}
                  </td>
                  <es-loader v-if="!getField('JOB_ASSIGNMENT_1')" class="iconCenter"/>
                  <template v-else>
                    <td>
                      <div v-if="editCurrentPanel">
                        <input
                          class="input"
                          type="text"
                          v-model="getField('JOB_ASSIGNMENT_1').customLabel"
                          :title="getField('JOB_ASSIGNMENT_1').customLabel"
                          aria-label="Display Label"
                        />
                      </div>
                      <div v-else class="disable-cursor">
                        <input
                          class="input"
                          :class="{ 'is-static': !editCurrentPanel }"
                          :readonly="!editCurrentPanel"
                          :value="getField('JOB_ASSIGNMENT_1').customLabel"
                          :title="getField('JOB_ASSIGNMENT_1').customLabel"
                          aria-label="Display Label"
                        />
                      </div>
                    </td>
                    <td>
                      <div v-if="editCurrentPanel">
                        <button
                          class="button button--form"
                          :disabled="getField('JOB_ASSIGNMENT_1').enabledReadOnly"
                          v-for="(option, index) in booleanOptions"
                          @click="getField('JOB_ASSIGNMENT_1').enabled = option.code;
                            validateRequiredButton('JOB_ASSIGNMENT_1')"
                          :class="{ 'is-active': getField('JOB_ASSIGNMENT_1')
                            .enabled === option.code }"
                          :key="index"
                        >
                          {{ option.description }}
                        </button>
                      </div>
                      <div v-else class="disable-cursor">
                        {{ getField('JOB_ASSIGNMENT_1').enabled === true ? 'Yes' : 'No' }}
                      </div>
                    </td>
                    <td>
                      <div v-if="editCurrentPanel">
                        <button
                          class="button button--form"
                          :disabled="getField('JOB_ASSIGNMENT_1').requiredReadOnly"
                          v-for="(option, index) in booleanOptions"
                          @click="getField('JOB_ASSIGNMENT_1').required = option.code;
                            validateRequiredButton('JOB_ASSIGNMENT_1', 'NOTENABLED')"
                          :class="{ 'is-active': getField('JOB_ASSIGNMENT_1')
                            .required === option.code }"
                          :key="index"
                        >
                          {{ option.description }}
                        </button>
                      </div>
                      <div v-else class="disable-cursor">
                        {{ getField('JOB_ASSIGNMENT_1').required === true ? 'Yes' : 'No' }}
                      </div>
                    </td>
                  </template>
                </tr>
                <tr :class="{ 'bottom-border': !editCurrentPanel }">
                  <td class="fields has-text-grey">
                    {{ getField('JOB_ASSIGNMENT_2').name }}
                  </td>
                  <es-loader v-if="!getField('JOB_ASSIGNMENT_2')" class="iconCenter"/>
                  <template v-else>
                    <td>
                      <div v-if="editCurrentPanel">
                        <input
                          class="input"
                          type="text"
                          v-model="getField('JOB_ASSIGNMENT_2').customLabel"
                          :title="getField('JOB_ASSIGNMENT_2').customLabel"
                          aria-label="Display Label"
                        />
                      </div>
                      <div v-else class="disable-cursor">
                        <input
                          class="input"
                          :class="{ 'is-static': !editCurrentPanel }"
                          :readonly="!editCurrentPanel"
                          :value="getField('JOB_ASSIGNMENT_2').customLabel"
                          :title="getField('JOB_ASSIGNMENT_2').customLabel"
                          aria-label="Display Label"
                        />
                      </div>
                    </td>
                    <td>
                      <div v-if="editCurrentPanel">
                        <button
                          class="button button--form"
                          :disabled="getField('JOB_ASSIGNMENT_2').enabledReadOnly"
                          v-for="(option, index) in booleanOptions"
                          @click="getField('JOB_ASSIGNMENT_2').enabled = option.code;
                            validateRequiredButton('JOB_ASSIGNMENT_2')"
                          :class="{ 'is-active': getField('JOB_ASSIGNMENT_2')
                            .enabled === option.code }"
                          :key="index"
                        >
                          {{ option.description }}
                        </button>
                      </div>
                      <div v-else class="disable-cursor">
                        {{ getField('JOB_ASSIGNMENT_2').enabled === true ? 'Yes' : 'No' }}
                      </div>
                    </td>
                    <td>
                      <div v-if="editCurrentPanel">
                        <button
                          class="button button--form"
                          :disabled="getField('JOB_ASSIGNMENT_2').requiredReadOnly"
                          v-for="(option, index) in booleanOptions"
                          @click="getField('JOB_ASSIGNMENT_2').required = option.code;
                            validateRequiredButton('JOB_ASSIGNMENT_2', 'NOTENABLED')"
                          :class="{ 'is-active': getField('JOB_ASSIGNMENT_2')
                            .required === option.code }"
                          :key="index"
                        >
                          {{ option.description }}
                        </button>
                      </div>
                      <div v-else class="disable-cursor">
                        {{ getField('JOB_ASSIGNMENT_2').required === true ? 'Yes' : 'No' }}
                      </div>
                    </td>
                  </template>
                </tr>
                <tr :class="{ 'bottom-border': !editCurrentPanel }">
                  <td class="fields has-text-grey">
                    {{ getField('JOB_ASSIGNMENT_3').name }}
                  </td>
                  <es-loader v-if="!getField('JOB_ASSIGNMENT_3')" class="iconCenter"/>
                  <template v-else>
                    <td>
                      <div v-if="editCurrentPanel">
                        <input
                          class="input"
                          type="text"
                          v-model="getField('JOB_ASSIGNMENT_3').customLabel"
                          :title="getField('JOB_ASSIGNMENT_3').customLabel"
                          aria-label="Display Label"
                        />
                      </div>
                      <div v-else class="disable-cursor">
                        <input
                          class="input"
                          :class="{ 'is-static': !editCurrentPanel }"
                          :readonly="!editCurrentPanel"
                          :value="getField('JOB_ASSIGNMENT_3').customLabel"
                          :title="getField('JOB_ASSIGNMENT_3').customLabel"
                          aria-label="Display Label"
                        />
                      </div>
                    </td>
                    <td>
                      <div v-if="editCurrentPanel">
                        <button
                          class="button button--form"
                          :disabled="getField('JOB_ASSIGNMENT_3').enabledReadOnly"
                          v-for="(option, index) in booleanOptions"
                          @click="getField('JOB_ASSIGNMENT_3').enabled = option.code;
                            validateRequiredButton('JOB_ASSIGNMENT_3')"
                          :class="{ 'is-active': getField('JOB_ASSIGNMENT_3')
                            .enabled === option.code }"
                          :key="index"
                        >
                          {{ option.description }}
                        </button>
                      </div>
                      <div v-else class="disable-cursor">
                        {{ getField('JOB_ASSIGNMENT_3').enabled === true ? 'Yes' : 'No' }}
                      </div>
                    </td>
                    <td>
                      <div v-if="editCurrentPanel">
                        <button
                          class="button button--form"
                          :disabled="getField('JOB_ASSIGNMENT_3').requiredReadOnly"
                          v-for="(option, index) in booleanOptions"
                          @click="getField('JOB_ASSIGNMENT_3').required = option.code;
                            validateRequiredButton('JOB_ASSIGNMENT_3', 'NOTENABLED')"
                          :class="{ 'is-active': getField('JOB_ASSIGNMENT_3')
                            .required === option.code }"
                          :key="index"
                        >
                          {{ option.description }}
                        </button>
                      </div>
                      <div v-else class="disable-cursor">
                        {{ getField('JOB_ASSIGNMENT_3').required === true ? 'Yes' : 'No' }}
                      </div>
                    </td>
                  </template>
                </tr>
                <tr>
                  <td class="fields has-text-grey">
                    {{ getField('ITINERANT_FLAG').name }}
                  </td>
                  <es-loader v-if="!getField('ITINERANT_FLAG')" class="iconCenter"/>
                  <template v-else>
                    <td>
                      <div v-if="editCurrentPanel">
                        <input
                          class="input"
                          type="text"
                          v-model="getField('ITINERANT_FLAG').customLabel"
                          :title="getField('ITINERANT_FLAG').customLabel"
                          aria-label="Display Label"
                        />
                      </div>
                      <div v-else class="disable-cursor">
                        <input
                          class="input"
                          :class="{ 'is-static': !editCurrentPanel }"
                          :readonly="!editCurrentPanel"
                          :value="getField('ITINERANT_FLAG').customLabel"
                          :title="getField('ITINERANT_FLAG').customLabel"
                          aria-label="Display Label"
                        />
                      </div>
                    </td>
                    <td>
                      <div v-if="editCurrentPanel">
                        <button
                          class="button button--form"
                          :disabled="getField('ITINERANT_FLAG').enabledReadOnly"
                          v-for="(option, index) in booleanOptions"
                          @click="getField('ITINERANT_FLAG').enabled = option.code;
                            validateRequiredButton('ITINERANT_FLAG')"
                          :class="{ 'is-active': getField('ITINERANT_FLAG')
                            .enabled === option.code }"
                          :key="index"
                        >
                          {{ option.description }}
                        </button>
                      </div>
                      <div v-else class="disable-cursor">
                        {{ getField('ITINERANT_FLAG').enabled === true ? 'Yes' : 'No' }}
                      </div>
                    </td>
                    <td>
                      <div v-if="editCurrentPanel">
                        <button
                          class="button button--form"
                          :disabled="getField('ITINERANT_FLAG').requiredReadOnly"
                          v-for="(option, index) in booleanOptions"
                          @click="getField('ITINERANT_FLAG').required = option.code;
                            validateRequiredButton('ITINERANT_FLAG', 'NOTENABLED')"
                          :class="{ 'is-active': getField('ITINERANT_FLAG')
                            .required === option.code }"
                          :key="index"
                        >
                          {{ option.description }}
                        </button>
                      </div>
                      <div v-else class="disable-cursor">
                        {{ getField('ITINERANT_FLAG').required === true ? 'Yes' : 'No' }}
                      </div>
                    </td>
                  </template>
                </tr>
              </tbody>
            </table>
          </template>
        </es-form>
      </div>
      <div
        v-if="editCurrentPanel"
        class="is-flex flex-justify--end flex-align--center"
        style="margin-right: 2rem; padding-bottom: 2rem"
      >
        <button
          class="button button-link has-underline"
          @click.prevent="cancelPanel()"
          style="margin-right: 1rem"
        >
          Cancel
        </button>
        <button
          class="button is-primary"
          @click.stop.prevent="checkRequiredFields()"
        >
          Update
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { library } from '@fortawesome/fontawesome-svg-core';
import {
  faAngleDoubleLeft,
  faCaretUp,
  faCaretDown,
  faEdit,
} from '@fortawesome/pro-regular-svg-icons';
import EsException from '@/components/EsException.vue';
import EsForm from '@/components/EsForm.vue';
import EsHeader from '@/components/EsHeader.vue';
import EsLoader from '@/components/EsLoader.vue';
import NotificationManager from '@/mixins/NotificationManager';
import PermissionManager from '@/mixins/PermissionManager';

library.add(
  faAngleDoubleLeft,
  faCaretUp,
  faCaretDown,
  faEdit,
);

export default {
  mixins: [
    NotificationManager,
    PermissionManager,
  ],
  components: {
    EsException,
    EsForm,
    EsHeader,
    EsLoader,
  },
  props: {
    receivedSpecificationType: {
      type: Object,
    },
  },
  data() {
    return this.initialData();
  },
  created() {
    this.onLoad();
    this.specsCleanup();
    this.booleanOptions = [
      {
        code: true,
        description: 'Yes',
        description2: 'Enabled',
      },
      {
        code: false,
        description: 'No',
        description2: 'Disabled',
      },
    ];
  },
  computed: {
    fieldLabelsRequestPayload() {
      // Create a Set of serialized field data from fieldsCached for quick lookup
      const cachedSet = new Set(
        this.fieldsCached.map(
          item => `${item.customLabel}-${item.required}-${item.enabled}-${item.fieldId}`,
        ),
      );
      // Filter the fieldsFiltered to find the fields not present in the cachedSet
      return this.fieldsFiltered.filter(
        item => !cachedSet.has(`${item.customLabel}-${item.required}-${item.enabled}-${item.fieldId}`),
      );
    },
  },
  methods: {
    cancelPanel() {
      this.clearErrors();
      this.filterAndCacheFields();
      this.undoSpecificationType();
      this.hideNotification();
      this.editCurrentPanel = false;
    },
    checkForSpecChanges() {
      return this.compareObjects(this.cachedSpecificationType, this.specificationType);
    },
    checkForFieldChanges() {
      return this.compareObjects(this.fieldsCached, this.fieldsFiltered);
    },
    checkRequiredFields() {
      // Validate each field individually
      this.validateField('header');
      this.validateField('detail');
      this.validateField('footer');
      // Check if any validation errors occurred
      const hasError = this.hasErrorHeader || this.hasErrorDetail || this.hasErrorFooter;
      if (hasError) {
        // Show error notification if there are validation errors
        this.showNotification('danger', ['There were some errors. Please fix the errors highlighted in the fields below.']);
      } else {
        // Hide notification, scroll to top, and proceed with updating specification
        this.hideNotification();
        window.scrollTo(0, 0);
        this.updateSpecification()
          .then((promiseResult) => {
            // Update field labels if updateSpecification succeeds
            if (promiseResult === true) {
              this.updateFieldLabels();
            }
          });
      }
    },
    clearErrors() {
      this.hideNotification();
      this.hasErrorHeader = false;
      this.msgHeader = null;
      this.hasErrorDetail = false;
      this.msgDetail = null;
      this.hasErrorFooter = false;
      this.msgFooter = null;
    },
    compareObjects(o, n) {
      return JSON.stringify(o) !== JSON.stringify(n);
    },
    editPanel() {
      this.hideNotification();
      this.editCurrentPanel = !this.editCurrentPanel;
      this.$emit('toggleAddExactMatchRule');
    },
    filterAndCacheFields() {
      const found = this.fields.map(item => ({
        name: item.field.name,
        code: item.field.code,
        customLabel: item.customLabel ? item.customLabel : null,
        customLength: item.customLength ? item.customLength : item.field.length,
        enabled: item.enabled,
        enabledReadOnly: item.enabledReadOnly,
        fieldId: item.fieldId,
        personTypeId: item.personTypeId,
        purpose: item.purpose,
        required: item.required,
        requiredReadOnly: item.requiredReadOnly,
      }));
      this.fieldsFiltered = found;
      this.fieldsCached = JSON.parse(JSON.stringify(this.fieldsFiltered));
      return this.fieldsFiltered;
    },
    getField(fieldCode) {
      return this.fieldsFiltered.find(f => f.code === fieldCode);
    },
    goBackToSubmissionSpecifications() {
      this.hideNotification();
      this.$emit('showSubmissionSpecifications');
    },
    initialData() {
      return {
        cachedSpecificationType: {},
        editCurrentPanel: false,
        loading: false,
        loadingError: false,
        personId: null,
        receivedPersonType: null,
        specId: null,
        specificationType: {},

        cdfInformation: true,
        contactInformation: true,
        credentialInformation: true,
        locationInformation: true,
        otherInformation: true,
        personInformation: true,

        hasErrorHeader: false,
        msgHeader: null,
        hasErrorDetail: false,
        msgDetail: null,
        hasErrorFooter: false,
        msgFooter: null,

        fields: [],
        fieldsCached: [],
        fieldsFiltered: [],
      };
    },
    async onLoad() {
      this.loading = true;
      this.loadingError = false;
      try {
        const res = await axios.get('field-mappings');
        this.fields = res.data
          .filter(
            n => n.personType.name === this.receivedSpecificationType.personType.name
              && n.purpose === this.receivedSpecificationType.purpose,
          );
        this.filterAndCacheFields();
        this.updateSpecificationCache();
      } catch (err) {
        this.loadingError = true;
      } finally {
        this.loading = false;
      }
    },
    preventSpecialCharacters(event) {
      const char = event.key;
      // Regular expression to match alphanumeric characters
      const alphanumericRegex = /^[a-zA-Z0-9]*$/;
      // Prevent input if the pressed key is not alphanumeric
      if (!alphanumericRegex.test(char)) {
        event.preventDefault();
      }
    },
    specsCleanup() {
      const {
        personType, id, fields, uniqueConstraint, ...rest
      } = this.receivedSpecificationType;
      this.specificationType = { ...rest };
      this.receivedPersonType = personType;
      this.personId = personType.id;
      this.specId = id;
    },
    toggleField(fieldName) {
      this[fieldName] = !this[fieldName];
    },
    validateRequiredButton(...params) {
      this.hideNotification();
      if (this.fieldsFiltered.find(f => f.code === params[0]).enabled === false) {
        this.fieldsFiltered.find(f => f.code === params[0]).required = false;
        if (params[1] === 'NOTENABLED') {
          this.showNotification('danger', ["Field must be 'Enabled' before making it 'Required' !!!"]);
        }
      }
    },
    undoSpecificationType() {
      this.specificationType = JSON.parse(JSON.stringify(this.cachedSpecificationType));
    },
    updateSpecificationCache() {
      this.cachedSpecificationType = JSON.parse(JSON.stringify(this.specificationType));
    },
    async updateSpecification() {
      // Return without firing API if there are no changes on the page from the User
      const changes = this.checkForSpecChanges();
      if (!changes) {
        this.editCurrentPanel = false;
        return true;
      }
      try {
        this.loading = true;
        this.loadingError = false;
        await axios({
          method: 'put',
          url: `/person-types/${this.personId}/submission-specs/${this.specId}`,
          data: this.specificationType,
        });
        this.updateSpecificationCache();
        this.showNotification('success', ['The Specifications Details have been updated successfully.']);
        this.editCurrentPanel = false;
        return true;
      } catch (error) {
        this.loadingError = true;
        const { status } = error.response;
        if (status === 400) {
          this.showNotification('danger', [`There was an issue updating Specifications. ${error.response.data.message}`]);
        } else {
          this.showNotification('danger', ['There was an issue updating Specifications. Please try again or contact your administrator.']);
        }
        return false;
      } finally {
        this.loading = false;
      }
    },
    async updateFieldLabels() {
      // Return without firing API if there are no changes on the page from the User
      const changes = this.checkForFieldChanges();
      if (!changes) {
        this.editCurrentPanel = false;
        return;
      }
      // Eliminate the 'code' from the payload and convert empty string to null
      this.fieldLabelsRequestPayload.map((item) => {
        const newItem = item;
        delete newItem.name;
        delete newItem.code;
        if (newItem.customLabel === '') {
          newItem.customLabel = null;
        }
        return newItem;
      });

      try {
        this.loading = true;
        this.loadingError = false;
        await axios({
          method: 'post',
          url: '/field-mappings',
          data: this.fieldLabelsRequestPayload,
        });
        this.showNotification('success', ['The Specifications Details have been updated successfully.']);
        this.editCurrentPanel = false;
      } catch (error) {
        this.loadingError = true;
        this.showNotification('danger', ['There was an issue updating Field Labels. Please try again or contact your administrator.']);
      } finally {
        this.onLoad();
        this.loading = false;
      }
    },
    validateField(field) {
      switch (field) {
        case 'header':
          this.validateFieldHelper(this.specificationType.headerInd, 'hasErrorHeader', 'msgHeader', 'Header Record Indicator is required.');
          break;
        case 'detail':
          this.validateFieldHelper(this.specificationType.detailInd, 'hasErrorDetail', 'msgDetail', 'Detailed Record Indicator is required.');
          break;
        case 'footer':
          this.validateFieldHelper(this.specificationType.footerInd, 'hasErrorFooter', 'msgFooter', 'Footer Record Indicator is required.');
          break;
        default:
          break;
      }
    },

    validateFieldHelper(value, errorProp, msgProp, errorMessage) {
      if (value === null || value === '') {
        this[errorProp] = true;
        this[msgProp] = errorMessage;
      } else {
        this[errorProp] = false;
        this[msgProp] = null;
      }
    },
  },
};
</script>

<style lang="sass" scoped>
  table th
    padding-left: 0
    vertical-align: middle
    border: 0
  table td
    border: 0
    vertical-align: middle
    padding-left: 0
    &:first-child
      width: 25%
    &:nth-child(2)
      width: 39%
    &:nth-child(3)
      width: 18%
    &:nth-child(4)
      width: 18%
  .bottom-border
    border-bottom: 1px solid #eee
  .details
    width: 70% !important
  .disable-cursor
    cursor: default
  .fields
    font-size: 12px
    padding-left: 1rem
  .header
    display: flex
    align-items: center
  .iconCenter
    font-size: 39px
    text-align: center
    vertical-align: -webkit-baseline-middle
    width: 100%
  .icon-right
    margin-left: auto
  .left-padding
    padding-left: 1rem
  .no-border
    border: 0
  .partition-border
    border-bottom: 1px solid #DEE2E6
    padding-bottom: 8px !important
    margin-right: 0 !important
    &:last-child
      margin-bottom: 15px !important
  .property
    padding-left: 3px
    font-size: 12px
    width: 70%
  .rule
    font-size: 12px
    color: #666
    padding-top: 1rem
  .top-padding
    padding-top: 1rem
  .content
    overflow-x: auto
  input
    text-overflow: ellipsis
</style>
